/**
 * 顾客端 DOM：菜单/分类/购物车弹层、平板横屏布局提示、登录与下单交互。
 * 经典脚本：先加载 app.js，再 html-templates（P5a-8：src/customer-app/html-templates.ts），再本文件；挂 window.initCustomerPage（支持 file://）。
 */
(function (g) {
  'use strict';
  /** 与 html-templates 的 DISH_IMG_EMPTY_SRC 一致：避免 img src 为空时旧 WebKit 加载当前页 */
  const DISH_IMG_EMPTY_PLACEHOLDER =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  const HtmlT = g.RestaurantHtmlTemplates;
  if (!HtmlT) {
    console.error('[RestaurantCustomerUi] 缺少 RestaurantHtmlTemplates，请先加载 customer 入口（含 html-templates）');
    return;
  }
  if (!g.RestaurantUiRender) {
    console.error('[RestaurantCustomerUi] 缺少 RestaurantUiRender，请先加载 js/ui-render.js');
    return;
  }

  const ORIENTATION_HINT_I18N = new Map([
  ['widePortrait', 'customer.rotateHintWidePortrait'],
  ['phoneLandscape', 'customer.rotateHintPhoneLandscape'],
]);

function orientationHintLayoutKey(wide, landscape) {
  if (wide && !landscape) return 'widePortrait';
  if (!wide && landscape) return 'phoneLandscape';
  return '';
}

function initCustomerPage() {
  /** 完整 init 末尾会替换；在此之前若已提交登录，至少切到主界面并尽量初始化菜单（缓解慢设备/异常顺序） */
  let runAfterCustomerLogin = () => {
    try {
      OS0?.ensureMenuInitialized?.();
    } catch (_) {}
    document.getElementById('customer-login-screen')?.classList.add('hidden');
    document.getElementById('customer-app')?.classList.remove('hidden');
    document.body.removeAttribute('data-customer-login-open');
  };
  const OS0 = window.Ordersystem;
  const Cart0 = window.RestaurantCartLogic;
  if (!OS0 || !Cart0) {
    console.error('[RestaurantCustomerUi] 脚本链未就绪：缺少 window.Ordersystem 或 window.RestaurantCartLogic（请检查 i18n/脚本是否加载成功）');
    return;
  }

  const pickCustomerApiBaseForLogin = () => {
    let b = '';
    try {
      b = (OS0.getNetworkConfig().apiBaseUrl || '').trim().replace(/\/$/, '');
    } catch (_) {}
    if (!b && /^https?:/i.test(location.protocol)) b = location.origin.trim().replace(/\/$/, '');
    return b;
  };

  const allocateCustomerGuestSlots = async (table, guests) => {
    const base = pickCustomerApiBaseForLogin();
    if (!base || typeof fetch !== 'function') return null;
    try {
      const r = await fetch(`${base}/api/table-occupy-allocate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ table, addGuests: guests }),
      });
      const j = await r.json();
      if (!r.ok || !j?.ok) return null;
      const totalGuests = parseInt(String(j.guests), 10);
      const startIndex = parseInt(String(j.startIndex ?? 0), 10);
      const assignedGuestSlots = Array.isArray(j.assignedGuestSlots)
        ? j.assignedGuestSlots.map((s) => String(s ?? '').trim()).filter(Boolean)
        : [];
      return {
        totalGuests: Number.isFinite(totalGuests) && totalGuests > 0 ? totalGuests : guests,
        startIndex: Number.isFinite(startIndex) && startIndex >= 0 ? startIndex : 0,
        assignedGuests: assignedGuestSlots.length || guests,
        guestName: String(j.guest || assignedGuestSlots[0] || '').trim(),
      };
    } catch (_) {
      return null;
    }
  };

  const loginFormEarly = document.getElementById('customer-login-form');
  if (loginFormEarly && !loginFormEarly.dataset.osLoginSubmitBound) {
    loginFormEarly.dataset.osLoginSubmitBound = '1';
    loginFormEarly.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const $loginErr = document.getElementById('customer-login-err');
      const $loginTable = document.getElementById('customer-login-table');
      const $loginGuests = document.getElementById('customer-login-guests');
      const $loginGuestValue = document.getElementById('customer-login-guest-value');
      const $loginSubmit = document.getElementById('customer-login-submit');
      $loginErr?.classList.add('hidden');
      if ($loginErr) $loginErr.textContent = '';
      const table = ($loginTable?.value || '').trim();
      const guests = parseInt(String($loginGuests?.value), 10);
      const guestName = ($loginGuestValue?.value || '').trim();
      const err = (msg) => {
        if ($loginErr) {
          $loginErr.textContent = msg;
          $loginErr.classList.remove('hidden');
        }
      };
      if (!table) return err(OS0.uiT('customer.loginErrTable'));
      if (!Number.isFinite(guests) || guests < 1 || guests > 99) return err(OS0.uiT('customer.loginErrGuests'));
      const labels = OS0.buildGuestSlotLabels(table, guests);
      if (!guestName || !labels.includes(guestName)) return err(OS0.uiT('customer.loginErrGuestPick'));
      if ($loginSubmit) $loginSubmit.disabled = true;
      try {
        const allocation = await allocateCustomerGuestSlots(table, guests);
        const totalGuests = allocation?.totalGuests || guests;
        const assignedGuest = allocation?.guestName || guestName;
        OS0.setCustomerSession(
          table,
          totalGuests,
          assignedGuest,
          allocation ? { startIndex: allocation.startIndex, guests: allocation.assignedGuests } : undefined
        );
        if (!allocation) {
          try {
            g.OS?.hooks?.notifyTableOccupied?.();
          } catch (_) {
            /* 后台「现时入席」与 bootstrap 的 POST /api/table-occupy 同逻辑 */
          }
        }
      } catch (e) {
        console.error('[RestaurantCustomerUi] setCustomerSession', e);
        if ($loginSubmit) $loginSubmit.disabled = false;
        return err(OS0.uiT('customer.loginErrTable'));
      }
      runAfterCustomerLogin();
    });
  }

  const loginLangSelEarly = document.getElementById('customer-login-lang-select');
  if (loginLangSelEarly && OS0 && !loginLangSelEarly.dataset.osLoginLangBound) {
    loginLangSelEarly.dataset.osLoginLangBound = '1';
    try {
      const v = OS0.getUiLang();
      if ([...loginLangSelEarly.options].some((o) => o.value === v)) loginLangSelEarly.value = v;
    } catch (_) {}
    loginLangSelEarly.addEventListener('change', () => {
      try {
        OS0.setUiLang(loginLangSelEarly.value);
      } catch (e) {
        console.error('[RestaurantCustomerUi] login lang select', e);
      }
    });
  }

  void (async () => {
      const OS = OS0;
      const Cart = Cart0;

      try {
        OS.ensureMenuInitialized();
      } catch (e) {
        console.error('[RestaurantCustomerUi] 菜单初始化失败', e);
      }

      const hydratePromise = OS.hydrateDefaultsFromServerIfNeeded()
        .then(() => {
          try {
            OS.ensureMenuInitialized();
          } catch (_) {}
        })
        .catch((e) => {
          console.error('[RestaurantCustomerUi] 拉取默认菜单/门店失败', e);
          try {
            OS.ensureMenuInitialized();
          } catch (_) {}
        })
        .finally(() => {
          try {
            OS.refreshCustomerTerminalTypeInSession?.();
          } catch (_) {}
        });

      const applyCustomerBackgroundPreset = () => {
        document.documentElement.setAttribute('data-customer-bg-preset', OS.getSystemSettings().customerBackgroundPreset);
      };
      applyCustomerBackgroundPreset();
      window.addEventListener('restaurantSystemSettingsUpdated', applyCustomerBackgroundPreset);
      window.addEventListener('storage', (e) => {
        if (e.key === 'restaurantSystemSettings') applyCustomerBackgroundPreset();
      });

      const renderComponent = (specs) => {
        for (const { el, html, after } of specs) {
          if (!el) continue;
          el.innerHTML = html;
          if (after) after(el);
        }
      };

      const fullMenu = () => OS.getMenu();
      let menu = fullMenu();
      let activeMeal = OS.getCustomerMealTab();
      let activeCat = 'all';
      /** 「全部」视图下左侧分类高亮（滚动联动）；筛选单类时与 activeCat 一致 */
      let categoryNavHighlightId = 'all';
      let pendingDetailDishId = null;
      let dishDetailQty = 1;
      let menuUi;
      const cart = Cart.createSessionCart();
      const esc = (s) => OS.escapeHtml(s);
      const escAttr = (s) =>
        String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
      let customerToastTimer = null;
      const showCustomerToast = (message, tone = 'success') => {
        const msg = String(message ?? '').trim();
        if (!msg) return;
        let el = document.getElementById('customer-toast');
        if (!el) {
          el = document.createElement('div');
          el.id = 'customer-toast';
          el.setAttribute('role', 'status');
          el.setAttribute('aria-live', 'polite');
          document.body.appendChild(el);
        }
        el.className = `customer-toast customer-toast--${tone}`;
        el.innerHTML =
          '<span class="customer-toast-dot" aria-hidden="true"></span>' +
          `<span>${esc(msg)}</span>`;
        requestAnimationFrame(() => el.classList.add('is-visible'));
        if (customerToastTimer) clearTimeout(customerToastTimer);
        customerToastTimer = setTimeout(() => {
          el.classList.remove('is-visible');
        }, 2200);
      };

      const $workbarLangMenu = document.getElementById('customer-workbar-lang-menu');
      const syncWorkbarLangMenu = () => {
        if (!$workbarLangMenu) return;
        const fallbackRows = [
          { code: 'de', abbr: 'DE', flag: 'assets/flags/de.svg' },
          { code: 'en', abbr: 'EN', flag: 'assets/flags/gb.svg' },
          { code: 'zh', abbr: 'ZH', flag: 'assets/flags/cn.svg' },
          { code: 'es', abbr: 'ES', flag: 'assets/flags/es.svg' },
          { code: 'fr', abbr: 'FR', flag: 'assets/flags/fr.svg' },
          { code: 'it', abbr: 'IT', flag: 'assets/flags/it.svg' },
          { code: 'ja', abbr: 'JP', flag: 'assets/flags/jp.svg' },
          { code: 'tr', abbr: 'TR', flag: 'assets/flags/tr.svg' },
        ];
        const rows = g.RestaurantAppConfig?.LANG_PICKER?.length ? g.RestaurantAppConfig.LANG_PICKER : fallbackRows;
        const cur = String(OS.getUiLang() || '');
        const baseCls =
          'os-press flex w-full items-center gap-2 px-3 py-3 text-left text-[0.96rem] font-normal transition-colors dark:text-neutral-100';
        const onCls =
          ' bg-brand-50 text-brand-800 ring-1 ring-inset ring-brand-500/35 dark:bg-brand-950/35 dark:text-brand-100 dark:ring-brand-500/45';
        const offCls = ' text-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800';
        $workbarLangMenu.innerHTML = rows
          .map((r) => {
            const code = String(r.code);
            const abbr = String(r.abbr || code).toUpperCase();
            const flag = String(r.flag || '').trim();
            const on = code === cur;
            return `<button type="button" role="option" data-set-lang="${escAttr(code)}" aria-selected="${on ? 'true' : 'false'}" class="${baseCls}${on ? onCls : offCls}"><img src="${escAttr(flag)}" alt="" class="h-[1.2rem] w-[2rem] shrink-0 rounded-sm object-cover ring-1 ring-black/10 dark:ring-white/10" /><span class="font-medium">${esc(abbr)}</span></button>`;
          })
          .join('');
      };
      syncWorkbarLangMenu();
      if ($workbarLangMenu && !$workbarLangMenu.dataset.osLangMenuBound) {
        $workbarLangMenu.dataset.osLangMenuBound = '1';
        $workbarLangMenu.addEventListener('click', (e) => {
          const b = e.target.closest('[data-set-lang]');
          if (!b) return;
          e.stopPropagation();
          OS.setUiLang(b.getAttribute('data-set-lang'));
          closeWorkbarModal();
        });
      }

      const $mainScroll = document.getElementById('customer-main-scroll');
      const $lines = document.getElementById('cart-lines');
      const $linesDesktop = document.getElementById('cart-lines-desktop');
      const $total = document.getElementById('cart-total');
      const $submit = document.getElementById('btn-submit-order');
      const $submitDesktop = document.getElementById('btn-submit-order-desktop');
      const $cartDrawer = document.getElementById('cart-drawer');
      const $panel = document.getElementById('cart-drawer-sheet');
      const $overlay = document.getElementById('cart-drawer-backdrop');
      const body = document.body;

      /**
       * iOS 12：仅用 setProperty 可能被 html.overflow-hidden + 含层叠上下文 的链干扰；
       * 用整段 style + fixed 全屏 + translate3d/-webkit-transform 强制抽屉露出。
       * z-index 须低于冷却锁层（打开时 style.css 保底 z-index:460）；否则下单冷却锁层被抽屉压在下面。
       */
      const CART_DRAWER_FORCE_Z = 255;
      const applyCartDrawerOpenVisuals = (on) => {
        if (!$cartDrawer || !$overlay || !$panel) return;
        if (on) {
          try {
            const h = typeof globalThis.innerHeight === 'number' ? globalThis.innerHeight : 640;
            const maxSheet = Math.min(Math.round(h * 0.92), 672);
            $cartDrawer.style.cssText =
              'position:fixed!important;left:0!important;top:0!important;right:0!important;bottom:0!important;width:100%!important;height:100%!important;max-width:100%!important;z-index:' +
              CART_DRAWER_FORCE_Z +
              '!important;pointer-events:auto!important;';
            $overlay.style.cssText = 'opacity:1!important;pointer-events:auto!important;';
            $panel.style.cssText =
              'position:absolute!important;left:0!important;right:0!important;bottom:0!important;max-height:' +
              maxSheet +
              'px!important;-webkit-transform:translate3d(0,0,0)!important;transform:translate3d(0,0,0)!important;pointer-events:auto!important;';
          } catch (_) {
            try {
              $cartDrawer.style.setProperty('pointer-events', 'auto', 'important');
              $overlay.style.setProperty('opacity', '1', 'important');
              $panel.style.setProperty('transform', 'translate3d(0,0,0)', 'important');
            } catch (_) {}
          }
        } else {
          try {
            $cartDrawer.style.cssText = '';
            $overlay.style.cssText = '';
            $panel.style.cssText = '';
          } catch (_) {}
        }
      };

      const prefersTouchCartDrawer = () => {
        try {
          if ('ontouchstart' in document.documentElement) return true;
        } catch (_) {}
        try {
          const mm = globalThis.matchMedia;
          if (typeof mm === 'function') {
            if (mm('(pointer: coarse)').matches) return true;
            if (mm('(hover: none)').matches) return true;
          }
        } catch (_) {}
        return false;
      };

      const $loginScreen = document.getElementById('customer-login-screen');
      const $customerApp = document.getElementById('customer-app');

      const isLoginScreenVisible = () =>
        !!($loginScreen && !$loginScreen.classList.contains('hidden'));

      /**
       * 刷新后若已带会话（尤其首屏即横屏 / Safari 地址栏尚未稳定），在 hydrate 完成前会长时间保持
       * body[data-customer-login-open] 与登录层未隐藏，叠放与冷却锁层逻辑都会错位。先于网络只做壳层同步。
       */
      const syncCustomerShellFromStoredSessionEarly = () => {
        try {
          if (!OS.getCustomerSession()) return;
          $loginScreen?.classList.add('hidden');
          $customerApp?.classList.remove('hidden');
          document.body.removeAttribute('data-customer-login-open');
        } catch (_) {}
      };
      syncCustomerShellFromStoredSessionEarly();

      const syncRootScrollLock = () => {
        const orderLocked = OS.getOrderCooldownRemainingMs() > 0 && !isLoginScreenVisible();
        /**
         * iOS / WebKit：html.overflow-hidden 会裁切或不绘制部分 position:fixed 全屏层（见购物车抽屉注释）。
         * 下单冷却锁层 #customer-order-lock-overlay 同为 fixed 全屏，勿因锁单把 html 锁 overflow，否则横屏
         *（尤其四边 safe-area 与可视高度紧张时）弹窗中部易被裁掉，表现为倒计时「不见」。
         */
        const needHtmlLock =
          body.dataset.dishDetailOpen === 'true' ||
          body.dataset.workbarModalOpen === 'true' ||
          body.dataset.cartFullscreenOpen === 'true' ||
          body.dataset.ordersQueryOpen === 'true' ||
          body.dataset.guestSwitchModalOpen === 'true';
        document.documentElement.classList.toggle('overflow-hidden', needHtmlLock);
        document.body.classList.toggle('os-order-cooldown-lock', orderLocked);
      };
      syncRootScrollLock();
      const $loginErr = document.getElementById('customer-login-err');
      const $loginTable = document.getElementById('customer-login-table');
      const $loginGuests = document.getElementById('customer-login-guests');
      const $loginGuestSlots = document.getElementById('customer-login-guest-slots');
      const $loginGuestValue = document.getElementById('customer-login-guest-value');
      const $guestSwitchWrapWb = document.getElementById('customer-guest-switch-wrap-workbar');
      const $guestSwitchBtnWb = document.getElementById('customer-guest-switch-btn-workbar');
      const $guestSwitchMenuWb = document.getElementById('customer-guest-switch-menu-workbar');
      const $guestAccountBtnsNarrow = document.getElementById('customer-guest-account-buttons-narrow');
      const $desktopMenuHost = document.getElementById('customer-workbar-desktop-menu-host');
      const $sidebarMenuHost = document.getElementById('customer-workbar-sidebar-menu-host');
      const $menuStackShelf = document.getElementById('customer-workbar-menu-stack-shelf');
      const $modalMenuHost = document.getElementById('customer-workbar-modal-menu-host');
      const $cartFullscreenModal = document.getElementById('customer-cart-fullscreen-modal');
      const $ordersQueryModal = document.getElementById('customer-orders-query-modal');
      const $guestSwitchModal = document.getElementById('customer-guest-switch-modal');
      const $workbarModal = document.getElementById('customer-workbar-modal');
      const $workbarModalTitle = document.getElementById('customer-workbar-modal-title');
      const WORKBAR_MODAL_TITLE_KEYS = {
        menu: 'customer.workbarMenuTitle',
        cart: 'customer.cartToggle',
        lang: 'customer.workbarLangTitle',
        account: 'customer.workbarAccountTitle',
      };

      const workbarModalTitleForTab = (tab) => {
        const t = String(tab || '');
        const tk = WORKBAR_MODAL_TITLE_KEYS[t];
        if (!tk) return '';
        const base = OS.uiT(tk);
        if (t !== 'account') return base;
        const sess = OS.getCustomerSession();
        const tbl = sess?.table != null ? String(sess.table).trim() : '';
        if (!tbl) return base;
        return `${base} · ${OS.uiT('customer.badgeTablePrefix')}${tbl}`;
      };

      const TERMINAL_TYPE_UI = {
        ipad_mini: 'customer.terminalIpadMini',
        ipad: 'customer.terminalIpad',
        iphone: 'customer.terminalIphone',
        android_phone: 'customer.terminalAndroidPhone',
        android_tablet: 'customer.terminalAndroidTablet',
        desktop: 'customer.terminalDesktop',
      };
      const terminalBadgeLabel = (sess) => {
        const tt = sess?.terminalType != null ? String(sess.terminalType).trim() : '';
        const key = TERMINAL_TYPE_UI[tt];
        if (key) return OS.uiT(key);
        if (!tt) return OS.uiT('customer.terminalIpadMini');
        return tt;
      };

      let tableCartSyncTimer = null;
      const TABLE_CART_SYNC_DEBOUNCE_MS = 450;

      const pickCustomerApiBase = () => {
        let b = (OS.getNetworkConfig().apiBaseUrl || '').trim().replace(/\/$/, '');
        if (!b && /^https?:/i.test(location.protocol)) b = location.origin.trim().replace(/\/$/, '');
        return b;
      };

      const pushTableCartToServer = async () => {
        const sess = OS.getCustomerSession();
        if (!sess) return;
        const base = pickCustomerApiBase();
        if (!base) return;
        const url = `${base}/api/table-cart-sync`;
        try {
          await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              table: sess.table,
              guests: sess.guests,
              carts: sess.carts || {},
              orderRound: sess.orderRound ?? 0,
              clientId: OS.getTableSyncClientId(),
            }),
          });
        } catch (_) {
          /* 离线或打印服务未开时忽略 */
        }
      };

      const scheduleTableCartSync = () => {
        if (tableCartSyncTimer) clearTimeout(tableCartSyncTimer);
        tableCartSyncTimer = setTimeout(() => {
          tableCartSyncTimer = null;
          void pushTableCartToServer();
        }, TABLE_CART_SYNC_DEBOUNCE_MS);
      };

      const hydrateTableCartFromServer = async () => {
        const sess = OS.getCustomerSession();
        if (!sess) return;
        const base = pickCustomerApiBase();
        if (!base) return;
        try {
          const u = new URL('/api/table-cart-state', base.endsWith('/') ? base : `${base}/`);
          u.searchParams.set('table', sess.table);
          const r = await fetch(u.href, { cache: 'no-store' });
          const j = await r.json();
          if (!j || !j.ok || j.empty) return;
          const ok = OS.applyRemoteTableCartSync({
            table: j.table,
            guests: j.guests,
            carts: j.carts,
            orderRound: j.orderRound,
          });
          if (!ok) return;
          const s2 = OS.getCustomerSession();
          if (s2) {
            cart.replaceFromRecord(s2.carts[s2.guestName] || {});
            cart.pruneToMenu(menu);
            void refreshMenuUi();
            renderCart();
            syncMenuDishQtyBadges();
            refreshSessionBadge();
          }
        } catch (_) {}
      };

      const applyRemoteTableCartFromWs = (msg) => {
        if (!msg || typeof msg !== 'object' || msg.type !== 'table-cart-sync') return;
        const fromSelf = String(msg.clientId || '') === String(OS.getTableSyncClientId());
        if (fromSelf) return;
        const ok = OS.applyRemoteTableCartSync({
          table: msg.table,
          guests: msg.guests,
          carts: msg.carts,
          orderRound: msg.orderRound,
        });
        if (!ok) return;
        const s2 = OS.getCustomerSession();
        if (!s2) return;
        cart.replaceFromRecord(s2.carts[s2.guestName] || {});
        cart.pruneToMenu(menu);
        void refreshMenuUi();
        renderCart();
        syncMenuDishQtyBadges();
        refreshSessionBadge();
        try {
          if (body.dataset.ordersQueryOpen === 'true') renderOrdersQueryBody();
        } catch (_) {}
      };

      const persistGuestCartNow = () => {
        const sess = OS.getCustomerSession();
        if (!sess) return;
        OS.persistCustomerGuestCart(sess.guestName, cart.toQuantityRecord());
        scheduleTableCartSync();
      };

      const syncLoginGuestSlots = () => {
        if (!$loginGuestSlots || !$loginGuestValue) return;
        const table = ($loginTable?.value || '').trim();
        const guests = parseInt(String($loginGuests?.value), 10);
        const g = Number.isFinite(guests) && guests >= 1 && guests <= 99 ? guests : 2;
        const labels = OS.buildGuestSlotLabels(table || '?', g);
        const aria = OS.uiT('customer.loginGuestPick');
        $loginGuestSlots.setAttribute('aria-label', aria);
        const prev = ($loginGuestValue.value || '').trim();
        $loginGuestSlots.innerHTML = '';
        const selCls =
          'ring-2 ring-brand-500 ring-offset-2 ring-offset-white dark:ring-offset-neutral-950 border-brand-500 bg-brand-50 dark:bg-brand-950/40';
        const baseCls =
          'os-press touch-manipulation mb-2 min-h-11 min-w-[2.75rem] rounded-lg border border-neutral-200/90 bg-white px-2.5 text-xs font-semibold tabular-nums text-neutral-700 shadow-sm transition active:opacity-70 hover:border-brand-400 hover:bg-brand-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:border-brand-500 dark:hover:bg-neutral-700 mr-[1ch]';
        let picked = labels.includes(prev) ? prev : labels[0] || '';
        labels.forEach((label) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.textContent = label;
          b.setAttribute('data-login-guest-slot', label);
          b.className = baseCls + (label === picked ? ' ' + selCls : '');
          b.addEventListener('click', () => {
            $loginGuestSlots.querySelectorAll('[data-login-guest-slot]').forEach((el) => {
              el.className = baseCls + (el === b ? ' ' + selCls : '');
            });
            $loginGuestValue.value = label;
          });
          $loginGuestSlots.appendChild(b);
        });
        $loginGuestValue.value = picked;
      };

      const guestSwitchMenus = () => [$guestSwitchMenuWb].filter(Boolean);
      const guestSwitchBtns = () => [$guestSwitchBtnWb].filter(Boolean);
      const switchableGuestSlots = (sess) => {
        const assigned = (sess?.assignedGuestSlots || []).map((s) => String(s ?? '').trim()).filter(Boolean);
        if (assigned.length) return assigned;
        return (sess?.guestSlots || []).map((s) => String(s ?? '').trim()).filter(Boolean);
      };

      const closeGuestSwitchMenu = () => {
        guestSwitchMenus().forEach((m) => m.classList.add('hidden'));
        guestSwitchBtns().forEach((b) => b.setAttribute('aria-expanded', 'false'));
      };

      const openGuestSwitchMenu = () => {
        const sess = OS.getCustomerSession();
        const menus = guestSwitchMenus();
        const btns = guestSwitchBtns();
        const slots = switchableGuestSlots(sess);
        if (!menus.length || !btns.length || !slots.length) return;
        const baseRow =
          'flex w-full items-center px-3 py-2 text-left text-[0.896rem] hover:bg-neutral-50 dark:hover:bg-neutral-800';
        const onPick = (slot) => {
          const cur = OS.getCustomerSession();
          if (!cur) return;
          const from = cur.guestName;
          const snap = cart.toQuantityRecord();
          const loaded = OS.applyCustomerGuestSwitch(from, snap, slot);
          cart.replaceFromRecord(loaded || {});
          closeGuestSwitchMenu();
          refreshSessionBadge();
          renderCart();
          syncMenuDishQtyBadges();
          scheduleTableCartSync();
        };
        menus.forEach((menu) => {
          menu.innerHTML = '';
          slots.forEach((slot) => {
            const row = document.createElement('button');
            row.type = 'button';
            row.setAttribute('role', 'option');
            row.setAttribute('data-switch-guest', slot);
            row.className =
              baseRow + (slot === sess.guestName ? ' font-semibold text-brand-700 dark:text-brand-300' : '');
            row.textContent = slot;
            row.addEventListener('click', () => onPick(slot));
            menu.appendChild(row);
          });
          menu.classList.remove('hidden');
        });
        btns.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      };

      let guestSwitchModalCallback = null;
      let ordersQueryGuestFilter = null;

      const guestNamesFromTableOrders = (table) => {
        const out = [];
        for (const o of OS.getOrders()) {
          if (!o || String(o.table ?? '') !== String(table ?? '')) continue;
          const g = String(o.guestName ?? '').trim();
          if (g) out.push(g);
        }
        return [...new Set(out)].sort((a, b) => a.localeCompare(b));
      };

      /** 订单查询：可选顾客 = 同台配置的 guestSlots；无多顾客配置时退回订单里出现过的账号名 */
      const ordersQueryFilterGuestOptions = (sess) => {
        if (!sess) return [];
        const slots = switchableGuestSlots(sess);
        if (slots.length) return [...new Set(slots)].sort((a, b) => a.localeCompare(b));
        return guestNamesFromTableOrders(sess.table);
      };

      const sanitizeOrdersQueryGuestFilter = (sess) => {
        if (ordersQueryGuestFilter == null || String(ordersQueryGuestFilter).trim() === '') return;
        const opts = ordersQueryFilterGuestOptions(sess);
        const g = String(ordersQueryGuestFilter).trim();
        if (g && !opts.includes(g)) ordersQueryGuestFilter = null;
      };

      const closeGuestSwitchModalPopup = () => {
        if (!$guestSwitchModal || $guestSwitchModal.classList.contains('hidden')) return;
        guestSwitchModalCallback = null;
        $guestSwitchModal.classList.add('hidden');
        $guestSwitchModal.setAttribute('aria-hidden', 'true');
        body.dataset.guestSwitchModalOpen = 'false';
        syncRootScrollLock();
      };

      const openGuestSwitchModalPopup = (afterPick) => {
        const sess = OS.getCustomerSession();
        const list = document.getElementById('customer-guest-switch-modal-list');
        if (!$guestSwitchModal || !list) return;
        if (!sess) return;
        const filterMode = typeof afterPick === 'function';
        const slotList = switchableGuestSlots(sess);
        if (!filterMode && slotList.length < 2) return;
        guestSwitchModalCallback = filterMode ? afterPick : null;
        const baseRow =
          'os-press flex w-full items-center rounded-xl px-3 py-3 text-left text-base hover:bg-neutral-50 dark:hover:bg-neutral-800';
        list.innerHTML = '';
        const slots = filterMode ? ordersQueryFilterGuestOptions(sess) : [...slotList];
        if (filterMode) {
          const allBtn = document.createElement('button');
          allBtn.type = 'button';
          allBtn.setAttribute('role', 'option');
          allBtn.className =
            baseRow +
            (ordersQueryGuestFilter == null || String(ordersQueryGuestFilter).trim() === ''
              ? ' font-semibold text-brand-700 dark:text-brand-300'
              : '');
          allBtn.textContent = OS.uiT('customer.catAll');
          allBtn.addEventListener('click', () => {
            afterPick(null);
            closeGuestSwitchModalPopup();
          });
          list.appendChild(allBtn);
        }
        slots.forEach((slot) => {
          const row = document.createElement('button');
          row.type = 'button';
          row.setAttribute('role', 'option');
          row.className =
            baseRow +
            (!filterMode && slot === sess.guestName ? ' font-semibold text-brand-700 dark:text-brand-300' : '') +
            (filterMode && String(ordersQueryGuestFilter ?? '') === String(slot) ? ' font-semibold text-brand-700 dark:text-brand-300' : '');
          row.textContent = slot;
          row.addEventListener('click', () => {
            if (guestSwitchModalCallback) {
              guestSwitchModalCallback(slot);
              closeGuestSwitchModalPopup();
              return;
            }
            const cur = OS.getCustomerSession();
            if (!cur) return;
            const from = cur.guestName;
            const snap = cart.toQuantityRecord();
            const loaded = OS.applyCustomerGuestSwitch(from, snap, slot);
            cart.replaceFromRecord(loaded || {});
            closeGuestSwitchModalPopup();
            closeGuestSwitchMenu();
            refreshSessionBadge();
            renderCart();
            syncMenuDishQtyBadges();
            scheduleTableCartSync();
          });
          list.appendChild(row);
        });
        const reveal = () => {
          if (!$guestSwitchModal) return;
          $guestSwitchModal.classList.remove('hidden');
          $guestSwitchModal.setAttribute('aria-hidden', 'false');
          body.dataset.guestSwitchModalOpen = 'true';
          syncRootScrollLock();
        };
        requestAnimationFrame(reveal);
      };

      /** 全屏购物车 / 底部抽屉：与侧栏同逻辑的「切换顾客账号」入口（打开同一弹层） */
      const syncCartPopupsGuestSwitchBtn = () => {
        const sess = OS.getCustomerSession();
        const showSwitch = switchableGuestSlots(sess).length > 1;
        const nm = String(sess?.guestName ?? '').trim();
        const aria = OS.uiT('customer.guestSwitchAria').replace(/\{\{name\}\}/g, nm || '');
        ['customer-cart-fs-guest-switch', 'cart-drawer-guest-switch'].forEach((id) => {
          const b = document.getElementById(id);
          if (!b) return;
          b.classList.toggle('hidden', !showSwitch);
          if (showSwitch) b.setAttribute('aria-label', aria);
        });
      };

      /** 与 Tailwind osTabletLand 一致：宽屏横屏布局（购物车栏等） */
      const isWideCartLayout = () =>
        globalThis.matchMedia && globalThis.matchMedia('(min-width: 744px) and (orientation: landscape)').matches;

      const isOsTabletMin744 = () =>
        !!(globalThis.matchMedia && globalThis.matchMedia('(min-width: 744px)').matches);

      const isTabletWorkbarSidebarExpanded = () => {
        const wb = document.getElementById('customer-workbar');
        return isOsTabletMin744() && !!wb;
      };

      /** 左栏已取消折叠，保留函数以免旧调用报错；始终维持展开 */
      const setCustomerWorkbarCollapsed = (_collapsed) => {
        const wb = document.getElementById('customer-workbar');
        if (!wb) return;
        wb.classList.remove('customer-workbar--collapsed');
        placeWorkbarMenuHost();
        try {
          void menuUi?.renderMenu?.();
        } catch (_) {}
        syncCustomerHeaderHeight();
      };

      const dockWorkbarMenuHostToShelf = () => {
        if (!$desktopMenuHost || !$menuStackShelf) return;
        if ($desktopMenuHost.parentElement !== $menuStackShelf) $menuStackShelf.appendChild($desktopMenuHost);
      };

      const mountWorkbarMenuHostInModal = () => {
        if (!$desktopMenuHost || !$modalMenuHost) return;
        if ($desktopMenuHost.parentElement !== $modalMenuHost) $modalMenuHost.appendChild($desktopMenuHost);
      };

      const mountWorkbarMenuHostToSidebar = () => {
        if (!$desktopMenuHost || !$sidebarMenuHost) return;
        if ($desktopMenuHost.parentElement !== $sidebarMenuHost) $sidebarMenuHost.appendChild($desktopMenuHost);
      };

      const placeWorkbarMenuHost = () => {
        if (body.dataset.workbarModalOpen === 'true' && workbarModalTab === 'menu') {
          mountWorkbarMenuHostInModal();
          return;
        }
        if (isTabletWorkbarSidebarExpanded() && $sidebarMenuHost) mountWorkbarMenuHostToSidebar();
        else dockWorkbarMenuHostToShelf();
      };

      let workbarModalTab = '';

      const setWorkbarModalPanels = (tab) => {
        const k = String(tab || '');
        document.getElementById('customer-workbar-modal-panel-menu')?.classList.toggle('hidden', k !== 'menu');
        document.getElementById('customer-workbar-modal-panel-cart')?.classList.toggle('hidden', k !== 'cart');
        document.getElementById('customer-workbar-modal-panel-lang')?.classList.toggle('hidden', k !== 'lang');
        document.getElementById('customer-workbar-modal-panel-account')?.classList.toggle('hidden', k !== 'account');
      };

      const closeWorkbarModal = () => {
        if (!$workbarModal || $workbarModal.classList.contains('hidden')) return;
        workbarModalTab = '';
        $workbarModal.classList.add('hidden');
        $workbarModal.setAttribute('aria-hidden', 'true');
        body.dataset.workbarModalOpen = 'false';
        placeWorkbarMenuHost();
        syncRootScrollLock();
      };

      const openWorkbarModal = (tab) => {
        const t = String(tab || '');
        if (!$workbarModal || !WORKBAR_MODAL_TITLE_KEYS[t]) return;
        workbarModalTab = t;
        setWorkbarModalPanels(t);
        if (t === 'menu') mountWorkbarMenuHostInModal();
        else placeWorkbarMenuHost();
        if ($workbarModalTitle) $workbarModalTitle.textContent = workbarModalTitleForTab(t);
        $workbarModal.classList.remove('hidden');
        $workbarModal.setAttribute('aria-hidden', 'false');
        body.dataset.workbarModalOpen = 'true';
        syncRootScrollLock();
        if (t === 'menu') {
          requestAnimationFrame(() => {
            applyCustomerBranding();
            requestAnimationFrame(() => applyCustomerBranding());
          });
        }
      };

      const updateLayoutChrome = () => {
        const $hint = document.getElementById('customer-orientation-hint');
        const wide = globalThis.matchMedia?.('(min-width: 744px) and (orientation: landscape)').matches;
        const landscape = globalThis.matchMedia?.('(orientation: landscape)').matches;
        if ($hint) {
          const layoutKey = orientationHintLayoutKey(!!wide, !!landscape);
          const i18nKey = layoutKey ? ORIENTATION_HINT_I18N.get(layoutKey) : '';
          const msg = i18nKey ? OS.uiT(i18nKey) : '';
          $hint.textContent = msg;
          $hint.classList.toggle('hidden', !msg);
        }
      };

      /** 顶栏含订单锁条时高度变化；供左侧栏 sticky / max-h 使用 */
      const syncCustomerHeaderHeight = () => {
        requestAnimationFrame(() => {
          const el = document.getElementById('customer-app-header');
          if (!el || el.offsetParent === null) return;
          document.documentElement.style.setProperty('--os-customer-header-ch', `${el.offsetHeight}px`);
        });
      };

      const NARROW_WORKBAR_COLLAPSE_LS = 'restaurantNarrowWorkbarCollapsed';
      /**
       * 业务约束：手机竖屏顶栏强制保持展开（绿底）。
       * 历史保留 collapse / expand 入口的事件绑定，但内部一律按 expanded 处理。
       */
      const applyNarrowWorkbarCollapsed = () => {
        const expanded = document.getElementById('customer-narrow-workbar-expanded');
        const expandBtn = document.getElementById('customer-narrow-workbar-expand');
        if (!expanded || !expandBtn) return;
        expanded.classList.remove('hidden');
        expandBtn.classList.add('hidden');
        try { localStorage.removeItem(NARROW_WORKBAR_COLLAPSE_LS); } catch (_) {}
        syncCustomerHeaderHeight();
      };

      const playAddToCartFly = (fromEl) => {
        try {
          if (!fromEl || globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
          const src = fromEl.getAttribute('src');
          if (!src?.trim()) return;
          const dock =
            (() => {
              const strip = document.getElementById('customer-workbar-strip-cart-open-btn');
              const rail = document.getElementById('customer-workbar-rail-cart-btn');
              const nb = document.getElementById('customer-narrow-cart-strip-btn');
              const wb = document.getElementById('customer-workbar-cart-btn');
              if (strip && strip.offsetParent != null) return strip;
              if (rail && rail.offsetParent != null) return rail;
              if (nb && nb.offsetParent != null) return nb;
              if (wb && wb.offsetParent != null) return wb;
              return document.getElementById('btn-mobile-selected-bar');
            })();
          if (!dock || dock.offsetParent === null) return;
          const a = fromEl.getBoundingClientRect();
          const b = dock.getBoundingClientRect();
          const fly = document.createElement('img');
          fly.src = src;
          fly.alt = '';
          fly.className =
            'fixed z-[200] h-14 w-14 rounded-full object-cover shadow-2xl ring-2 ring-white/90 pointer-events-none will-change-transform';
          fly.style.left = `${a.left + a.width / 2 - 28}px`;
          fly.style.top = `${a.top + a.height / 2 - 28}px`;
          const dx = b.left + b.width / 2 - (a.left + a.width / 2);
          const dy = b.top + b.height / 2 - (a.top + a.height / 2);
          document.body.appendChild(fly);
          fly.getBoundingClientRect();
          requestAnimationFrame(() => {
            fly.style.transition =
              'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.5s ease';
            fly.style.transform = `translate(${dx}px, ${dy}px) scale(0.32)`;
            fly.style.opacity = '0.12';
          });
          setTimeout(() => {
            fly.remove();
            dock.classList.add('os-cart-dock-pulse');
            setTimeout(() => dock.classList.remove('os-cart-dock-pulse'), 450);
          }, 580);
        } catch (_) {}
      };

      const maxItemsAllowed = () => {
        const { maxItemsPerOrder: n } = OS.getSystemSettings();
        return Number.isFinite(n) && n > 0 ? n : 0;
      };
      const cartTotalQty = () => cart.totalQuantity();

      /** 本台所有顾客账号购物车份数之和 */
      const sumTableCartQty = () => {
        const sess = OS.getCustomerSession();
        if (!sess?.guestSlots?.length) return cart.totalQuantity();
        const live = cart.toQuantityRecord();
        let n = 0;
        sess.guestSlots.forEach((slot) => {
          const rec = slot === sess.guestName ? live : (sess.carts?.[slot] ?? {});
          Object.keys(rec).forEach((id) => {
            const q = parseInt(String(rec[id]), 10);
            if (Number.isFinite(q) && q > 0) n += q;
          });
        });
        return n;
      };

      /** 登录时「同桌客人数量」，与 guestSlots 一致 */
      const tableGuestCount = () => {
        const sess = OS.getCustomerSession();
        if (!sess) return 1;
        const g = parseInt(String(sess.guests), 10);
        if (Number.isFinite(g) && g >= 1) return Math.min(99, g);
        const sl = sess.guestSlots?.length ?? 0;
        return sl >= 1 ? Math.min(99, sl) : 1;
      };

      /** 系统「每账号上限」× 同桌人数；0 表示不设总台上限 */
      const maxItemsTableTotal = () => {
        const per = maxItemsAllowed();
        return per <= 0 ? 0 : per * tableGuestCount();
      };

      const formatMaxItemsTableMsg = () =>
        OS.uiT('customer.maxItemsTableReached')
          .replace('{{n}}', String(maxItemsTableTotal()))
          .replace('{{g}}', String(tableGuestCount()))
          .replace('{{m}}', String(maxItemsAllowed()));

      const detailQtyMaxAdd = () => {
        const perCap = maxItemsAllowed();
        const tableCap = maxItemsTableTotal();
        if (perCap <= 0 && tableCap <= 0) return 99;
        let room = 99;
        if (perCap > 0) room = Math.min(room, Math.max(0, perCap - cartTotalQty()));
        if (tableCap > 0) room = Math.min(room, Math.max(0, tableCap - sumTableCartQty()));
        return room;
      };

      const alertIfCartIncBlocked = () => {
        const maxG = maxItemsAllowed();
        if (maxG > 0 && cartTotalQty() >= maxG) {
          alert(OS.uiT('customer.maxItemsReached').replace('{{n}}', String(maxG)));
          return true;
        }
        const maxT = maxItemsTableTotal();
        if (maxT > 0 && sumTableCartQty() >= maxT) {
          alert(formatMaxItemsTableMsg());
          return true;
        }
        return false;
      };
      const syncDishDetailQtyUi = () => {
        if (body.dataset.dishDetailOpen !== 'true') return;
        const locked = OS.getOrderCooldownRemainingMs() > 0;
        const cap = detailQtyMaxAdd();
        const dec = document.getElementById('dish-detail-qty-dec');
        const inc = document.getElementById('dish-detail-qty-inc');
        const val = document.getElementById('dish-detail-qty-val');
        const addBtn = document.getElementById('dish-detail-add');
        if (locked || cap <= 0) {
          if (val) val.textContent = String(Math.max(1, dishDetailQty));
          if (dec) dec.disabled = true;
          if (inc) inc.disabled = true;
          if (addBtn) addBtn.disabled = true;
          return;
        }
        dishDetailQty = Math.min(Math.max(1, dishDetailQty), cap);
        if (val) val.textContent = String(dishDetailQty);
        if (dec) dec.disabled = dishDetailQty <= 1;
        if (inc) inc.disabled = dishDetailQty >= cap;
        if (addBtn) addBtn.disabled = false;
      };
      const catName = (id) => {
        const c = fullMenu().categories.find((x) => x.id === id);
        return c ? OS.categoryLabelForUi(c) : id;
      };
      const applyDishDetailNameVegStyle = (d) => {
        const nameEl = document.getElementById('dish-detail-name');
        if (!nameEl) return;
        nameEl.className = 'text-lg font-bold leading-snug text-neutral-900 dark:text-white sm:text-xl';
      };
      const applyDishDetailFoodClassBadge = (d) => {
        const el = document.getElementById('dish-detail-food-class');
        if (!el) return;
        const text = String(OS.dishFoodClassLabelForUi(d) ?? '').trim();
        if (!text) {
          el.textContent = '';
          el.classList.add('hidden');
          return;
        }
        el.textContent = text;
        el.classList.remove('hidden');
        const fc = d && d.foodClass != null ? String(d.foodClass).trim() : '';
        const base = 'mt-0.5 text-[10px] font-semibold uppercase tracking-wider ';
        el.className =
          base +
          (fc === 'veg'
            ? 'text-emerald-600 dark:text-emerald-400'
            : fc === 'meat'
              ? 'text-amber-600 dark:text-amber-500'
              : fc === 'fish'
                ? 'text-sky-600 dark:text-sky-400'
                : 'text-neutral-500 dark:text-neutral-400');
      };
      const syncDishDetailNoteAndAllergens = (d) => {
        const menuRef = fullMenu();
        const descEl = document.getElementById('dish-detail-desc');
        const noteBlock = document.getElementById('dish-detail-note-block');
        const noteText = OS.dishDetailNoteForUi(menuRef, d);
        const trimmed = String(noteText ?? '').trim();
        const veg = OS.dishFoodClassIsVeg(d);
        if (descEl) {
          if (trimmed) {
            descEl.textContent = trimmed;
            descEl.className =
              'mt-1 whitespace-pre-line text-sm leading-relaxed ' +
              (veg ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-600 dark:text-neutral-400');
          } else {
            descEl.textContent = OS.uiT('customer.dishDetailNoteEmpty');
            descEl.className =
              'mt-1 whitespace-pre-line text-sm leading-relaxed italic text-neutral-400/80 dark:text-neutral-500';
          }
        }
        noteBlock?.classList.remove('hidden');
        const lines = OS.dishAllergenLabelsForUi(d);
        const ablock = document.getElementById('dish-detail-allergens-block');
        const ul = document.getElementById('dish-detail-allergens');
        if (!ablock || !ul) return;
        if (!lines.length) {
          ablock.classList.add('hidden');
          ul.innerHTML = '';
          return;
        }
        ablock.classList.remove('hidden');
        ul.innerHTML = lines.map((t) => `<li>${esc(t)}</li>`).join('');
      };
      /** 语言或菜单变更时刷新详情浮层文案（不重置数量） */
      const syncDishDetailLabelsFromMenu = () => {
        if (body.dataset.dishDetailOpen !== 'true' || pendingDetailDishId == null) return;
        const d = fullMenu().dishes.find((x) => String(x?.id) === String(pendingDetailDishId));
        if (!d) return;
        const menuRef = fullMenu();
        const img = document.getElementById('dish-detail-img');
        const nameDe = OS.dishNameForReceiptLang(menuRef, d.id, 'de', d.name);
        if (img) img.alt = nameDe;
        const nameEl = document.getElementById('dish-detail-name');
        if (nameEl) nameEl.textContent = nameDe;
        applyDishDetailNameVegStyle(d);
        applyDishDetailFoodClassBadge(d);
        syncDishDetailNoteAndAllergens(d);
        const priceEl = document.getElementById('dish-detail-price');
        const priceRow = document.getElementById('dish-detail-price-row');
        const pn = Number(d?.price);
        const showPrice = Number.isFinite(pn) && pn !== 0;
        if (priceEl) priceEl.textContent = showPrice ? '€' + d.price : '';
        priceRow?.classList.toggle('hidden', !showPrice);
      };

      const updateThemeToggleLabel = () => {
        const dark = document.documentElement.classList.contains('dark');
        const label = dark ? OS.uiT('customer.themeToLight') : OS.uiT('customer.themeToDark');
        document.querySelectorAll('.js-theme-toggle').forEach((btn) => btn.setAttribute('aria-label', label));
      };

      /**
       * 管理端未配置 logoUrl 时的兜底图：默认 Yuoki Logo。
       * 若想改换他店品牌，直接改这里一处即可；仍让 admin 输入值优先。
       */
      const DEFAULT_BRAND_LOGO_URL = '/Yuoki-Logo.jpg?v=1';

      const applyCustomerBranding = () => {
        const { name, logoUrl, website } = OS.getRestaurantInfo();
        const displayName = (name || 'YUOKI GmbH').trim() || 'YUOKI GmbH';
        const initial = (displayName.charAt(0) || 'Y').toUpperCase();
        const applyLogoBlock = (img, fb) => {
          if (!img || !fb) return;
          const configured = OS.resolveCustomerMediaUrl((logoUrl || '').trim());
          const u = configured || DEFAULT_BRAND_LOGO_URL;
          const showLetter = () => {
            img.onload = null;
            img.onerror = null;
            try {
              img.src = DISH_IMG_EMPTY_PLACEHOLDER;
            } catch (_) {}
            img.classList.add('hidden');
            fb.textContent = initial;
            fb.classList.remove('hidden');
          };
          if (u) {
            img.onload = () => {
              img.classList.remove('hidden');
              fb.classList.add('hidden');
            };
            img.onerror = () => {
              showLetter();
            };
            img.classList.add('hidden');
            fb.textContent = initial;
            fb.classList.remove('hidden');
            img.src = u;
            /** 已由缓存就绪时 WebKit 可能不触发 load，需同步揭开；否则侧栏 fixed 下偶发整图不绘 */
            if (img.complete && img.naturalWidth > 0) {
              img.onload = null;
              img.onerror = null;
              img.classList.remove('hidden');
              fb.classList.add('hidden');
            }
          } else {
            showLetter();
          }
        };
        const applyWebsite = (a) => {
          if (!a) return;
          const w = (website || '').trim();
          if (w) {
            let href = w;
            if (!/^https?:\/\//i.test(href)) href = 'https://' + href;
            a.href = href;
            a.textContent = w.replace(/^https?:\/\//i, '').replace(/\/$/, '') || w;
            a.classList.remove('hidden');
          } else {
            a.removeAttribute('href');
            a.textContent = '';
            a.classList.add('hidden');
          }
        };
        applyLogoBlock(document.getElementById('customer-workbar-logo'), document.getElementById('customer-workbar-logo-fallback'));
        document.getElementById('customer-workbar-logo')?.setAttribute('alt', displayName);
        const wbName = document.getElementById('customer-workbar-brand-name');
        if (wbName) wbName.textContent = displayName;
        applyWebsite(document.getElementById('customer-workbar-website'));

        applyLogoBlock(document.getElementById('customer-narrow-brand-logo'), document.getElementById('customer-narrow-brand-logo-fallback'));
        document.getElementById('customer-narrow-brand-logo')?.setAttribute('alt', displayName);
        const nbName = document.getElementById('customer-narrow-brand-name');
        if (nbName) nbName.textContent = displayName;
        applyWebsite(document.getElementById('customer-narrow-brand-website'));
      };

      /** 手机账号弹层：顾客账号平铺为按钮（无下拉） */
      function renderNarrowAccountGuestButtons(sess) {
        const el = $guestAccountBtnsNarrow;
        if (!el) return;
        el.innerHTML = '';
        const slots = switchableGuestSlots(sess);
        if (!slots.length) {
          el.classList.add('hidden');
          return;
        }
        el.classList.remove('hidden');
        const baseCls =
          'os-press flex min-h-[2.5rem] w-full min-w-0 items-center justify-center truncate rounded-xl border border-neutral-200/90 bg-white/90 px-1.5 py-2 text-center text-[0.68rem] font-semibold leading-tight text-neutral-800 shadow-sm active:opacity-70 dark:border-neutral-600 dark:bg-neutral-800/90 dark:text-neutral-100 sm:min-h-[2.75rem] sm:px-2 sm:text-[0.72rem]';
        const cur = String(sess?.guestName ?? '').trim();
        slots.forEach((slot) => {
          const s = String(slot ?? '').trim();
          if (!s) return;
          const b = document.createElement('button');
          b.type = 'button';
          b.className =
            baseCls +
            (s === cur
              ? ' ring-2 ring-brand-500/35 border-brand-500/60 dark:border-brand-500/50 dark:ring-brand-400/30'
              : '');
          b.textContent = s;
          b.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const curS = OS.getCustomerSession();
            if (!curS || s === String(curS.guestName ?? '').trim()) return;
            const from = curS.guestName;
            const snap = cart.toQuantityRecord();
            const loaded = OS.applyCustomerGuestSwitch(from, snap, s);
            cart.replaceFromRecord(loaded || {});
            refreshSessionBadge();
            renderCart();
            syncMenuDishQtyBadges();
          });
          el.appendChild(b);
        });
      }

      const refreshSessionBadge = () => {
        const sess = OS.getCustomerSession();
        const chipWb = document.getElementById('customer-table-chip-workbar');
        const subWb = document.getElementById('customer-session-subline-workbar');
        const chipN = document.getElementById('customer-table-chip-narrow');
        const subN = document.getElementById('customer-session-subline-narrow');
        const sideTbl = document.getElementById('customer-sidebar-table-readonly');
        const sideGuestOpen = document.getElementById('customer-sidebar-guest-switch-open');
        if (!sess) {
          chipWb?.classList.add('hidden');
          subWb?.classList.add('hidden');
          chipN?.classList.add('hidden');
          subN?.classList.add('hidden');
          $guestSwitchWrapWb?.classList.add('hidden');
          $guestAccountBtnsNarrow?.classList.add('hidden');
          if ($guestAccountBtnsNarrow) $guestAccountBtnsNarrow.innerHTML = '';
          closeGuestSwitchMenu();
          if (sideTbl) sideTbl.textContent = '—';
          sideGuestOpen?.classList.add('hidden');
          return;
        }
        if (sideTbl) sideTbl.textContent = String(sess.table ?? '—');
        const nm = sess.guestName != null ? String(sess.guestName).trim() : '';
        const nextRound = (sess.orderRound ?? 0) + 1;
        const roundStr = String(OS.uiT('customer.badgeRound')).replace(/\{\{n\}\}/g, String(nextRound));
        const tableLine = OS.uiT('customer.badgeTablePrefix') + sess.table;
        const term = terminalBadgeLabel(sess);
        const subText =
          term +
          ' · ' +
          (nm ? nm + ' · ' : '') +
          sess.guests +
          OS.uiT('customer.badgeGuestsSuffix') +
          ' · ' +
          roundStr;
        if (chipWb) {
          chipWb.classList.remove('hidden');
          chipWb.textContent = tableLine;
        }
        if (chipN) {
          chipN.classList.remove('hidden');
          chipN.textContent = tableLine;
        }
        const showSwitch = switchableGuestSlots(sess).length > 1;
        if ($guestSwitchWrapWb && $guestSwitchBtnWb && showSwitch) {
          $guestSwitchWrapWb.classList.remove('hidden');
          $guestSwitchBtnWb.textContent = nm || OS.uiT('customer.guestSwitch');
          $guestSwitchBtnWb.setAttribute(
            'aria-label',
            OS.uiT('customer.guestSwitchAria').replace(/\{\{name\}\}/g, nm || '')
          );
        } else {
          $guestSwitchWrapWb?.classList.add('hidden');
        }
        renderNarrowAccountGuestButtons(sess);
        if (!showSwitch) closeGuestSwitchMenu();
        sideGuestOpen?.classList.toggle('hidden', !showSwitch);
        if (subWb) {
          subWb.classList.remove('hidden');
          subWb.textContent = subText;
        }
        if (subN) {
          subN.classList.remove('hidden');
          subN.textContent = subText;
        }
        const tableBannerMob = document.getElementById('cart-panel-table-banner');
        const tableBannerDesk = document.getElementById('cart-desktop-table-banner');
        tableBannerMob?.classList.add('hidden');
        tableBannerDesk?.classList.add('hidden');
        renderRightQuickbarGuest(sess);
        if (body.dataset.workbarModalOpen === 'true' && workbarModalTab && $workbarModalTitle) {
          $workbarModalTitle.textContent = workbarModalTitleForTab(workbarModalTab);
        }
      };

      /**
       * 右栏 Quickbar 顾客账号按钮：显示当前账号 + 同台所有账号下拉选择。
       * - 单顾客时按钮仅显示账号名、点击不出菜单（无意义）。
       * - 多顾客时点击展开菜单，再点账号 → 通过既有 OS.applyCustomerGuestSwitch 切换并刷新 UI。
       */
      const closeRightQuickbarGuestMenu = () => {
        const m = document.getElementById('customer-right-quickbar-guest-menu');
        const t = document.getElementById('customer-right-quickbar-guest-trigger');
        if (m) {
          m.classList.add('hidden');
          m.setAttribute('aria-hidden', 'true');
        }
        if (t) t.setAttribute('aria-expanded', 'false');
      };
      const renderRightQuickbarGuest = (sess) => {
        const trigger = document.getElementById('customer-right-quickbar-guest-trigger');
        const label = document.getElementById('customer-right-quickbar-guest-label');
        const menu = document.getElementById('customer-right-quickbar-guest-menu');
        if (!trigger || !label || !menu) return;
        const slots = switchableGuestSlots(sess);
        const cur = sess?.guestName ? String(sess.guestName).trim() : (slots[0] || '');
        label.textContent = cur || '—';
        if (slots.length <= 1) {
          trigger.setAttribute('aria-disabled', slots.length === 0 ? 'true' : 'false');
          closeRightQuickbarGuestMenu();
          menu.innerHTML = '';
          return;
        }
        trigger.setAttribute('aria-disabled', 'false');
        const html = slots
          .map((s) => {
            const on = s === cur;
            return (
              '<button type="button" data-right-quick-guest-pick="' +
              OS.escapeHtml(s) +
              '" role="option" aria-selected="' +
              (on ? 'true' : 'false') +
              '" class="os-press mb-1 last:mb-0 flex w-full items-center justify-center rounded-full border px-3 py-2 text-sm font-semibold tabular-nums transition ' +
              (on
                ? 'border-brand-400/70 bg-brand-500/20 text-brand-200 ring-1 ring-brand-400/30'
                : 'border-slate-600/70 bg-slate-800/80 text-slate-200 hover:border-brand-400/45 hover:bg-slate-700/85 hover:text-white') +
              '">' +
              OS.escapeHtml(s) +
              '</button>'
            );
          })
          .join('');
        menu.innerHTML = html;
      };

      const LOGIN_MEAL_BTN_BASE =
        'os-press rounded-xl border px-2 py-2.5 text-center text-xs font-semibold shadow-sm transition active:opacity-70 sm:text-sm';
      const LOGIN_MEAL_BTN_ON =
        ' border-brand-500 bg-brand-50 text-brand-800 ring-2 ring-brand-500/25 dark:border-brand-500 dark:bg-brand-950/40 dark:text-brand-100';
      const LOGIN_MEAL_BTN_OFF =
        ' border-neutral-200/90 bg-white text-neutral-800 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100';

      const syncLoginMealUi = () => {
        const wrap = document.getElementById('customer-login-meal-wrap');
        const tabs = document.getElementById('customer-login-meal-tabs');
        if (!wrap || !tabs) return;
        const s = OS.getSystemSettings();
        const useMeal = OS.systemSettingsUseMealProfiles(s);
        const ov = s.customerMealOverride != null ? String(s.customerMealOverride).trim() : '';
        const locked = ov === 'lunch' || ov === 'dinner' || ov === 'special';
        if (!useMeal || locked) {
          wrap.classList.add('hidden');
          return;
        }
        wrap.classList.remove('hidden');
        tabs.setAttribute('aria-label', OS.uiT('customer.mealTabsAria'));
        const current = OS.getCustomerMealTab();
        tabs.querySelectorAll('[data-login-meal]').forEach((btn) => {
          const id = btn.getAttribute('data-login-meal');
          const on = id === current;
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          btn.className = LOGIN_MEAL_BTN_BASE + (on ? LOGIN_MEAL_BTN_ON : LOGIN_MEAL_BTN_OFF);
        });
      };

      const refreshCustomerStaticUi = () => {
        OS.applyUiLabels(document);
        syncWorkbarLangMenu();
        const rn = (OS.getRestaurantInfo().name || '').trim();
        document.title = rn ? rn + ' · ' + OS.uiT('customer.menuTitle') : OS.uiT('customer.docTitle');
        OS.updateLangSwitcherUI('customer');
        updateThemeToggleLabel();
        updateLayoutChrome();
        const label = OS.uiT('customer.lang');
        [
          'customer-workbar-lang-btn',
          'customer-login-lang-select',
        ].forEach((id) => {
          const lb = document.getElementById(id);
          if (lb) lb.setAttribute('aria-label', label);
        });
        const cartOpenLabel = OS.uiT('customer.cartToggle');
        [
          'customer-workbar-cart-btn',
          'customer-narrow-cart-strip-btn',
          'customer-workbar-strip-cart-open-btn',
          'customer-workbar-rail-cart-btn',
        ].forEach((id) => {
          const c = document.getElementById(id);
          if (c) c.setAttribute('aria-label', cartOpenLabel);
        });
        document.querySelectorAll('[data-workbar-modal-open="cart"]').forEach((el) => {
          el.setAttribute('aria-label', cartOpenLabel);
        });
        document.querySelectorAll('[data-workbar-modal-open="menu"]').forEach((el) => {
          el.setAttribute('aria-label', OS.uiT('customer.workbarMenuTitle'));
        });
        document.querySelectorAll('[data-workbar-modal-open="lang"]').forEach((el) => {
          el.setAttribute('aria-label', OS.uiT('customer.workbarLangTitle'));
        });
        document.getElementById('customer-orders-query-guest-btn')?.setAttribute(
          'aria-label',
          OS.uiT('customer.ordersQueryFilterGuest')
        );
        document.getElementById('customer-sidebar-guest-switch-open')?.setAttribute(
          'aria-label',
          OS.uiT('customer.sidebarGuestSwitchOpen')
        );
        document.getElementById('customer-narrow-workbar-collapse')?.setAttribute(
          'aria-label',
          OS.uiT('customer.narrowWorkbarCollapseBtn')
        );
        syncLoginGuestSlots();
        syncLoginMealUi();
        refreshSessionBadge();
        syncCustomerHeaderHeight();
        if (body.dataset.ordersQueryOpen === 'true') {
          syncOrdersQueryGuestBtn();
          syncOrdersQueryGuestMenu();
        }
      };

      const visibleDishes = () => {
        const m = fullMenu();
        const s = OS.getSystemSettings();
        if (!OS.systemSettingsUseMealProfiles(s)) return m.dishes || [];
        return OS.filterDishesByMealProfile(m, OS.getCustomerMealTab(), s);
      };

      const visibleCategories = () => {
        const m = fullMenu();
        const catIds = new Set((visibleDishes() || []).map((d) => String(d?.category ?? '')));
        return (m.categories || []).filter((c) => catIds.has(String(c?.id ?? '')));
      };

      const pickValidMealTab = () => {
        const s = OS.getSystemSettings();
        if (!OS.systemSettingsUseMealProfiles(s)) return;
        activeMeal = OS.getCustomerMealTab();
        const ov = s.customerMealOverride != null ? String(s.customerMealOverride).trim() : '';
        if (ov === 'lunch' || ov === 'dinner' || ov === 'special') return;
        const m = fullMenu();
        if (OS.filterDishesByMealProfileStrict(m, activeMeal, s).length) return;
        for (const key of ['lunch', 'dinner', 'special']) {
          if (OS.filterDishesByMealProfileStrict(m, key, s).length) {
            activeMeal = key;
            OS.setCustomerMealTab(activeMeal);
            return;
          }
        }
      };

      function syncMenuDishQtyBadges() {
        const menuRef = fullMenu();
        const { rows } = cart.buildRenderSnapshot(menuRef);
        const byId = new Map(rows.map((r) => [String(r.d.id), r.qty]));
        document.querySelectorAll('[data-dish-card]').forEach((card) => {
          const id = card.getAttribute('data-dish-card');
          const q = byId.get(String(id ?? '')) ?? 0;
          const media = card.querySelector('[data-dish-card-media]');
          if (!media) return;
          let badge = media.querySelector('[data-os-dish-cart-qty]');
          if (q <= 0) {
            badge?.remove();
            return;
          }
          if (!badge) {
            badge = document.createElement('span');
            badge.setAttribute('data-os-dish-cart-qty', '');
            badge.className =
              'pointer-events-none absolute right-1.5 top-1.5 z-[2] flex h-6 min-w-[1.5rem] max-w-[2.5rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[11px] font-bold leading-none text-white shadow-md ring-2 ring-white/90 dark:bg-brand-500 sm:right-2 sm:top-2';
            media.appendChild(badge);
          }
          badge.textContent = q > 99 ? '99+' : String(q);
        });
      }

      const setCartOpen = (open) => {
        const clearSheetDrag = () => {
          $panel?.style.removeProperty('transition');
          if (!open) $panel?.style.removeProperty('transform');
        };
        const on = !!open;
        if (on && OS.getOrderCooldownRemainingMs() > 0) {
          updateLockBanner();
          return;
        }
        if (on) {
          try {
            document.body.removeAttribute('data-customer-login-open');
          } catch (_) {}
        }
        body.dataset.cartOpen = on ? 'true' : 'false';
        try {
          body.setAttribute('data-cart-open', on ? 'true' : 'false');
        } catch (_) {}
        $cartDrawer?.setAttribute('aria-hidden', on ? 'false' : 'true');
        $cartDrawer?.classList.toggle('pointer-events-none', !on);
        $overlay?.setAttribute('aria-hidden', on ? 'false' : 'true');
        $panel?.setAttribute('aria-hidden', on ? 'false' : 'true');
        clearSheetDrag();
        applyCartDrawerOpenVisuals(on);
        syncRootScrollLock();
      };

      let prevWideCartLayout = null;
      const onLayoutViewportChange = () => {
        const wideNow = isWideCartLayout();
        if (prevWideCartLayout !== null && prevWideCartLayout !== wideNow) setCartOpen(false);
        prevWideCartLayout = wideNow;
        placeWorkbarMenuHost();
        updateLayoutChrome();
        syncCustomerHeaderHeight();
      };

      const setDishDetailOpen = (on) => {
        body.dataset.dishDetailOpen = on ? 'true' : 'false';
        document.getElementById('dish-detail-overlay')?.setAttribute('aria-hidden', on ? 'false' : 'true');
        syncRootScrollLock();
      };

      const closeDishDetail = () => {
        pendingDetailDishId = null;
        dishDetailQty = 1;
        setDishDetailOpen(false);
      };

      const openDishDetail = (dishId) => {
        if (OS.getOrderCooldownRemainingMs() > 0) return;
        const d = fullMenu().dishes.find((x) => String(x?.id) === String(dishId));
        if (!d) return;
        pendingDetailDishId = dishId;
        const img = document.getElementById('dish-detail-img');
        const menuRef = fullMenu();
        if (img) {
          const src = String(OS.resolveCustomerMediaUrl(d.imageUrl || d.img || '') || '').trim();
          img.src = src || DISH_IMG_EMPTY_PLACEHOLDER;
          img.alt = OS.dishNameForReceiptLang(menuRef, d.id, 'de', d.name);
        }
        const nameEl = document.getElementById('dish-detail-name');
        const priceEl = document.getElementById('dish-detail-price');
        if (nameEl) nameEl.textContent = OS.dishNameForReceiptLang(menuRef, d.id, 'de', d.name);
        applyDishDetailNameVegStyle(d);
        applyDishDetailFoodClassBadge(d);
        syncDishDetailNoteAndAllergens(d);
        const pn = Number(d.price);
        const showPrice = Number.isFinite(pn) && pn !== 0;
        if (priceEl) priceEl.textContent = showPrice ? '€' + d.price : '';
        document.getElementById('dish-detail-price-row')?.classList.toggle('hidden', !showPrice);
        dishDetailQty = 1;
        setDishDetailOpen(true);
        syncDishDetailQtyUi();
      };

      const bindCartQty = (root) => {
        if (!root) return;
        root.querySelectorAll('[data-dec]').forEach((b) => {
          b.addEventListener('click', () => {
            if (OS.getOrderCooldownRemainingMs() > 0) return;
            cart.decrement(b.getAttribute('data-dec'));
            renderCart();
          });
        });
        root.querySelectorAll('[data-inc]').forEach((b) => {
          b.addEventListener('click', () => {
            if (OS.getOrderCooldownRemainingMs() > 0) return;
            if (alertIfCartIncBlocked()) return;
            cart.increment(b.getAttribute('data-inc'));
            renderCart();
          });
        });
        root.querySelectorAll('[data-del]').forEach((b) => {
          b.addEventListener('click', () => {
            if (OS.getOrderCooldownRemainingMs() > 0) return;
            cart.remove(b.getAttribute('data-del'));
            renderCart();
          });
        });
      };

      function updateLockBanner() {
        const overlay = document.getElementById('customer-order-lock-overlay');
        const titleEl = document.getElementById('customer-order-lock-title');
        const cdEl = document.getElementById('customer-order-lock-countdown');
        const subEl = document.getElementById('customer-order-lock-sub');
        if (!overlay) return;
        const ms = OS.getOrderCooldownRemainingMs();
        const wasLocked = !overlay.classList.contains('hidden');
        const showLockUi = ms > 0 && !isLoginScreenVisible();
        if (ms <= 0 || !showLockUi) {
          overlay.classList.add('hidden');
          overlay.setAttribute('aria-hidden', 'true');
          if (titleEl) titleEl.textContent = '';
          if (cdEl) cdEl.textContent = '';
          if (subEl) subEl.textContent = '';
          if (wasLocked && ms <= 0) {
            void menuUi.renderMenu();
            renderCart();
          }
          syncRootScrollLock();
          syncCustomerHeaderHeight();
          syncCartCooldownHints();
          return;
        }
        setCartOpen(false);
        try {
          document.body.removeAttribute('data-customer-login-open');
        } catch (_) {}
        overlay.classList.remove('hidden');
        overlay.setAttribute('aria-hidden', 'false');
        closeWorkbarModal();
        closeCartFullscreen();
        closeOrdersQueryModal();
        closeGuestSwitchModalPopup();
        if (titleEl) titleEl.textContent = OS.uiT('customer.orderLockBanner');
        const sec = Math.max(0, Math.ceil(ms / 1000));
        const mm = Math.floor(sec / 60);
        const ss = sec % 60;
        const pad2 = (n) => (n < 10 ? '0' : '') + String(n);
        if (cdEl) cdEl.textContent = pad2(mm) + ':' + pad2(ss);
        if (subEl) subEl.textContent = OS.uiT('customer.orderLockSub');
        syncRootScrollLock();
        syncCustomerHeaderHeight();
        syncCartCooldownHints();
      }

      const refreshMenuUi = async () => {
        await menuUi.renderMenu();
        renderCart();
      };

      menuUi = g.RestaurantUiRender.createCustomerMenuUi({
        OS,
        esc,
        getActiveCat: () => activeCat,
        getCategoryNavActiveId: () => {
          if (activeCat !== 'all') return activeCat;
          if (categoryNavHighlightId !== 'all') return categoryNavHighlightId;
          const cats = visibleCategories();
          return cats[0]?.id != null ? String(cats[0].id) : 'all';
        },
        setCategoryNavHighlight: (id) => {
          if (activeCat !== 'all') return;
          const cats = visibleCategories();
          const first = cats[0]?.id != null ? String(cats[0].id) : 'all';
          if (!id || id === 'all') categoryNavHighlightId = first;
          else categoryNavHighlightId = id;
        },
        setActiveCat: (v) => {
          activeCat = v;
          const cats = visibleCategories();
          const first = cats[0]?.id != null ? String(cats[0].id) : 'all';
          categoryNavHighlightId = v === 'all' ? first : v;
        },
        getActiveMeal: () => activeMeal,
        setActiveMeal: (v) => {
          activeMeal = v;
          OS.setCustomerMealTab(activeMeal);
        },
        syncActiveMealFromOs: () => {
          activeMeal = OS.getCustomerMealTab();
        },
        refreshMenuAndCart: refreshMenuUi,
        updateLockBanner,
        openDishDetail,
        maxItemsAllowed,
        cartTotalQty,
        cartAddBlockedByLimits: () => {
          const per = maxItemsAllowed();
          const cap = maxItemsTableTotal();
          if (per <= 0 && cap <= 0) return false;
          if (per > 0 && cartTotalQty() >= per) return true;
          if (cap > 0 && sumTableCartQty() >= cap) return true;
          return false;
        },
        catName,
        visibleDishes,
        visibleCategories,
        pickValidMealTab,
        mainScrollEl: () => document.getElementById('customer-main-scroll'),
        afterMenuDomUpdated: syncMenuDishQtyBadges,
        afterMenuCategoryActivated: () => {
          closeWorkbarModal();
        },
      });

      (() => {
        const sel = document.getElementById('customer-login-lang-select');
        if (!sel) return;
        const flagPair = (a, b) => String.fromCodePoint(a, b);
        /** 与 LANG_PICKER 一致；原生 <option> 无法用 <img>，用区旗 emoji 在下拉列表中显示图标 */
        const LOGIN_LANG_FLAG_EMOJI = {
          de: flagPair(0x1f1e9, 0x1f1ea),
          en: flagPair(0x1f1ec, 0x1f1e7),
          zh: flagPair(0x1f1e8, 0x1f1f3),
          es: flagPair(0x1f1ea, 0x1f1f8),
          fr: flagPair(0x1f1eb, 0x1f1f7),
          it: flagPair(0x1f1ee, 0x1f1f9),
          ja: flagPair(0x1f1ef, 0x1f1f5),
          tr: flagPair(0x1f1f9, 0x1f1f7),
        };
        const loginLangOptionLabel = (r) => {
          const code = String(r.code);
          const emoji = LOGIN_LANG_FLAG_EMOJI[code] || '';
          const abbr = String(r.abbr);
          return emoji ? `${emoji} ${abbr}` : abbr;
        };
        const fallbackRows = [
          { code: 'de', abbr: 'DE' },
          { code: 'en', abbr: 'EN' },
          { code: 'zh', abbr: 'ZH' },
          { code: 'es', abbr: 'ES' },
          { code: 'fr', abbr: 'FR' },
          { code: 'it', abbr: 'IT' },
          { code: 'ja', abbr: 'JP' },
          { code: 'tr', abbr: 'TR' },
        ];
        const rows = g.RestaurantAppConfig?.LANG_PICKER?.length ? g.RestaurantAppConfig.LANG_PICKER : fallbackRows;
        const fillOptions = () => {
          const cur = OS.getUiLang();
          sel.innerHTML = rows
            .map(
              (r) =>
                `<option value="${esc(String(r.code))}">${esc(loginLangOptionLabel(r))}</option>`
            )
            .join('');
          if (rows.some((x) => String(x.code) === String(cur))) sel.value = cur;
        };
        fillOptions();
        if (!sel.dataset.osLoginLangBound) {
          sel.dataset.osLoginLangBound = '1';
          sel.addEventListener('change', () => OS.setUiLang(sel.value));
        }
        const onLangBundle = () => {
          fillOptions();
          refreshCustomerStaticUi();
          void refreshMenuUi();
        };
        window.addEventListener('restaurantUiLangUpdated', onLangBundle);
        window.addEventListener('restaurantOsUiLocaleLoaded', onLangBundle);
      })();

      const orderCooldownHumanHint = () => {
        const ms = OS.getOrderCooldownRemainingMs();
        if (ms <= 0) return '';
        const sec = Math.max(0, Math.ceil(ms / 1000));
        const mm = Math.floor(sec / 60);
        const ss = sec % 60;
        return OS.uiT('customer.orderLockRemain')
          .replace('{{m}}', String(mm))
          .replace('{{s}}', String(ss).padStart(2, '0'));
      };

      const syncCartCooldownHints = () => {
        const ms = OS.getOrderCooldownRemainingMs();
        const els = [
          document.getElementById('customer-cart-fs-cooldown'),
          document.getElementById('cart-drawer-cooldown'),
          document.getElementById('cart-desktop-cooldown'),
        ].filter(Boolean);
        if (ms <= 0) {
          els.forEach((el) => {
            el.classList.add('hidden');
            el.textContent = '';
          });
          return;
        }
        const hint = orderCooldownHumanHint();
        const block = [OS.uiT('customer.orderLockBanner'), hint, OS.uiT('customer.orderLockSub')]
          .filter(Boolean)
          .join('\n');
        els.forEach((el) => {
          el.textContent = block;
          el.classList.remove('hidden');
        });
      };

      const closeCartFullscreen = () => {
        if (!$cartFullscreenModal || $cartFullscreenModal.classList.contains('hidden')) return;
        $cartFullscreenModal.classList.add('hidden');
        $cartFullscreenModal.setAttribute('aria-hidden', 'true');
        body.dataset.cartFullscreenOpen = 'false';
        try {
          body.setAttribute('data-cart-fullscreen-open', 'false');
        } catch (_) {}
        cartFsGestureLockUntil = 0;
        syncRootScrollLock();
      };

      const renderCartFullscreenBody = () => {
        persistGuestCartNow();
        const el = document.getElementById('customer-cart-fs-body');
        const menuRef = fullMenu();
        if (!el) return;
        const tableQty = sumTableCartQty();
        const remain = OS.getOrderCooldownRemainingMs();
        const cartLocked = remain > 0;
        const sess = OS.getCustomerSession();
        const slots = switchableGuestSlots(sess);

        const renderOneGuestBlock = (slot, snapRows, snapSum, isCurrent, riBase, idx, slotCount) => {
          const inner =
            snapRows.length === 0
              ? `<p class="text-xs italic text-neutral-400 dark:text-neutral-500">${esc(OS.uiT('customer.cartEmpty'))}</p>`
              : snapRows
                  .map((r, i) =>
                    HtmlT.customerCartLineRow(esc, {
                      ri: riBase + i,
                      name: OS.dishNameForUiLang(menuRef, r.d),
                      qty: r.qty,
                      line: r.line,
                      dishId: r.d.id,
                      imageUrl: OS.resolveCustomerMediaUrl(r.d.imageUrl || r.d.img || ''),
                      cartLocked: cartLocked || !isCurrent,
                      readOnly: !isCurrent,
                      foodClass: r.d.foodClass,
                    })
                  )
                  .join('');
          /**
           * 始终按 2 列左右排版（即便只有一个顾客也是 2 列里的左侧）。
           * 当前顾客高亮：浅色品牌底 + 粗框，便于在多人列表里一眼看到「我」。
           * 第二列起加左分隔线，区隔各顾客块。
           */
          const oneCol = slotCount < 2;
          const span = oneCol ? 'col-span-2' : '';
          const sep =
            !oneCol && idx % 2 === 1
              ? 'border-l border-neutral-200/80 pl-3 dark:border-neutral-700/80'
              : !oneCol && idx % 2 === 0
                ? 'pr-1'
                : '';
          const headTone = isCurrent
            ? 'bg-brand-50 text-brand-800 dark:bg-brand-950/40 dark:text-brand-100 ring-1 ring-brand-500/30'
            : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800/70 dark:text-neutral-300';
          const wrapAttrs = isCurrent && snapRows.length ? ' data-cart-fs-current="true"' : '';
          const sumNum = Number(snapSum);
          const sumLine =
            Number.isFinite(sumNum) && sumNum < 0
              ? ''
              : `<p class="mt-2 text-right text-sm font-semibold text-neutral-900 dark:text-white">€${esc(String(snapSum))}</p>`;
          return (
            `<section class="min-w-0 rounded-xl ${span} ${sep}">` +
            `<h3 class="mb-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-extrabold tabular-nums shadow-sm ${headTone}">` +
            esc(String(slot)) +
            (isCurrent ? ' <span class="text-[0.65rem] font-bold uppercase tracking-wider opacity-80">·now</span>' : '') +
            `</h3>` +
            `<div${wrapAttrs} class="space-y-0">${inner}</div>` +
            sumLine +
            `</section>`
          );
        };

        if (!slots.length) {
          const { rows } = cart.buildRenderSnapshot(menuRef);
          el.innerHTML =
            rows.length === 0
              ? HtmlT.customerCartEmptyMessage(esc, OS.uiT('customer.cartEmpty'))
              : rows
                  .map((r, ri) =>
                    HtmlT.customerCartLineRow(esc, {
                      ri,
                      name: OS.dishNameForUiLang(menuRef, r.d),
                      qty: r.qty,
                      line: r.line,
                      dishId: r.d.id,
                      imageUrl: OS.resolveCustomerMediaUrl(r.d.imageUrl || r.d.img || ''),
                      cartLocked,
                      foodClass: r.d.foodClass,
                    })
                  )
                  .join('');
          if (rows.length) bindCartQty(el);
        } else {
          const live = cart.toQuantityRecord();
          const tmpCart = Cart.createSessionCart();
          let riBase = 0;
          const parts = slots.map((slot, idx) => {
            const rec = slot === sess.guestName ? live : (sess.carts?.[slot] ?? {});
            tmpCart.replaceFromRecord(rec);
            const snap = tmpCart.buildRenderSnapshot(menuRef);
            const block = renderOneGuestBlock(
              slot,
              snap.rows,
              snap.sum,
              slot === sess.guestName,
              riBase,
              idx,
              slots.length
            );
            riBase += Math.max(snap.rows.length, 1);
            return block;
          });
          /**
           * 始终 2 列，左右布局：每个顾客的购物车并排显示。
           * 即使只有 1 个顾客，也会自动 col-span-2 全宽展开（见 renderOneGuestBlock 里的 oneCol）。
           * 即使该顾客购物车为空，也会显示标题占位，方便对比同台所有顾客的下单情况。
           */
          el.innerHTML = `<div class="grid grid-cols-2 items-start gap-3">${parts.join('')}</div>`;
          const curWrap = el.querySelector('[data-cart-fs-current="true"]');
          if (curWrap) bindCartQty(curWrap);
        }

        const sub = document.getElementById('customer-cart-fs-submit');
        if (sub) sub.disabled = tableQty <= 0 || remain > 0;
        syncCartCooldownHints();
        syncCartPopupsGuestSwitchBtn();
      };

      /** 旧 WebKit 上 touchstart + touchend + click 可能连发；用绝对时间窗口去重，关闭弹层后立刻可再开 */
      const CART_FS_GESTURE_DEDUP_MS = 280;
      let cartFsGestureLockUntil = 0;
      const openCartFullscreenFromUserGesture = () => {
        const now = Date.now();
        if (now < cartFsGestureLockUntil) return;
        cartFsGestureLockUntil = now + CART_FS_GESTURE_DEDUP_MS;
        openCartFullscreen();
      };

      const openCartFullscreen = () => {
        if (!$cartFullscreenModal) return;
        if (OS.getOrderCooldownRemainingMs() > 0) {
          updateLockBanner();
          return;
        }
        closeWorkbarModal();
        try {
          document.body.removeAttribute('data-customer-login-open');
        } catch (_) {}
        /** iPad/手机：全屏模态在旧 WebKit 上不可靠，直接走底部抽屉 + 内联强制可见 */
        if (prefersTouchCartDrawer()) {
          body.dataset.cartFullscreenOpen = 'false';
          try {
            body.setAttribute('data-cart-fullscreen-open', 'false');
          } catch (_) {}
          if (!$cartFullscreenModal.classList.contains('hidden')) {
            $cartFullscreenModal.classList.add('hidden');
            $cartFullscreenModal.setAttribute('aria-hidden', 'true');
          }
          try {
            renderCart();
          } catch (_) {}
          setCartOpen(true);
          return;
        }
        /** 先于去 .hidden，让 style.css 末尾「强制可见」能压过残留的 data-customer-login-open */
        body.dataset.cartFullscreenOpen = 'true';
        try {
          body.setAttribute('data-cart-fullscreen-open', 'true');
        } catch (_) {}
        try {
          renderCartFullscreenBody();
        } catch (err) {
          console.error('[RestaurantCustomerUi] renderCartFullscreenBody', err);
        }
        $cartFullscreenModal.classList.remove('hidden');
        $cartFullscreenModal.setAttribute('aria-hidden', 'false');
        syncRootScrollLock();
        try {
          void $cartFullscreenModal.offsetHeight;
        } catch (_) {}
        /** 非触摸：若全屏层仍不可见，降级为底部抽屉 */
        const tryDrawerFallback = () => {
          try {
            if ($cartFullscreenModal.classList.contains('hidden')) return;
            const st = window.getComputedStyle($cartFullscreenModal);
            const r = $cartFullscreenModal.getBoundingClientRect();
            const bad =
              st.visibility === 'hidden' ||
              Number(st.opacity) < 0.05 ||
              st.display === 'none' ||
              r.width < 32 ||
              r.height < 32;
            if (!bad) return;
            closeCartFullscreen();
            setCartOpen(true);
            try {
              renderCart();
            } catch (_) {}
          } catch (_) {}
        };
        setTimeout(tryDrawerFallback, 140);
      };

      const closeOrdersQueryGuestDropdown = () => {
        const menu = document.getElementById('customer-orders-query-guest-menu');
        const btn = document.getElementById('customer-orders-query-guest-btn');
        menu?.classList.add('hidden');
        menu?.setAttribute('aria-hidden', 'true');
        btn?.setAttribute('aria-expanded', 'false');
      };

      const syncOrdersQueryGuestBtn = () => {
        const sess = OS.getCustomerSession();
        const btn = document.getElementById('customer-orders-query-guest-btn');
        const labelEl = document.getElementById('customer-orders-query-guest-btn-label');
        if (!btn || !sess) return;
        const f =
          ordersQueryGuestFilter != null && String(ordersQueryGuestFilter).trim()
            ? String(ordersQueryGuestFilter).trim()
            : '';
        const text = f
          ? `${OS.uiT('customer.ordersQueryFilterGuest')}：${f}`
          : `${OS.uiT('customer.ordersQueryFilterGuest')}（${OS.uiT('customer.catAll')}）`;
        if (labelEl) labelEl.textContent = text;
        else btn.textContent = text;
      };

      const syncOrdersQueryGuestMenu = () => {
        const menu = document.getElementById('customer-orders-query-guest-menu');
        if (!menu) return;
        const sess = OS.getCustomerSession();
        if (!sess) {
          menu.innerHTML = '';
          return;
        }
        const opts = ordersQueryFilterGuestOptions(sess);
        const allOn = ordersQueryGuestFilter == null || String(ordersQueryGuestFilter).trim() === '';
        const rowBase =
          'os-press w-full px-3 py-2.5 text-left text-sm font-medium text-neutral-800 hover:bg-neutral-50 dark:text-neutral-100 dark:hover:bg-neutral-800';
        const rowOn = ' bg-brand-50 font-semibold text-brand-800 dark:bg-brand-950/45 dark:text-brand-200';
        const parts = [
          `<button type="button" role="option" data-orders-query-guest="__all__" class="${rowBase}${allOn ? rowOn : ''}">${esc(OS.uiT('customer.catAll'))}</button>`,
        ];
        opts.forEach((name) => {
          const sel = !allOn && String(ordersQueryGuestFilter ?? '') === name;
          parts.push(
            `<button type="button" role="option" data-orders-query-guest="${escAttr(encodeURIComponent(name))}" class="${rowBase}${sel ? rowOn : ''}">${esc(name)}</button>`
          );
        });
        menu.innerHTML = parts.join('');
      };

      /**
       * 订单预览（已删除顾客筛选下拉）：
       *   按 *顾客* 左右并排显示，每列内自上而下列出该顾客最近 2 轮订单（轮次新→旧）。
       *   - 列源：sess.guestSlots（同台所有顾客）；当前顾客高亮，无下单的顾客显示空提示。
       *   - 始终 grid-cols-2，与购物车弹窗一致；
       *     单顾客时该列 col-span-2 占满；
       *     ≥3 顾客时按 2 列网格自动换行。
       */
      const renderOrdersQueryBody = () => {
        const sess = OS.getCustomerSession();
        const el = document.getElementById('customer-orders-query-body');
        if (!el) return;
        if (!sess) {
          el.innerHTML = '';
          return;
        }
        const all = OS.getOrders().filter((o) => o && String(o.table ?? '') === String(sess.table));
        const rounds = [
          ...new Set(
            all
              .map((o) => parseInt(String(o.orderRound ?? 0), 10))
              .filter((n) => Number.isFinite(n) && n > 0)
          ),
        ].sort((a, b) => b - a);
        const pick = rounds.slice(0, 2);
        const list = all.filter((o) => pick.includes(parseInt(String(o.orderRound ?? 0), 10)));
        const slots = switchableGuestSlots(sess);
        const renderSlots = slots.length
          ? slots
          : Array.from(
              new Set(list.map((o) => String(o.guestName ?? '').trim()).filter(Boolean))
            );
        if (!renderSlots.length) {
          el.innerHTML = `<p class="text-center text-neutral-500 dark:text-neutral-400">${esc(OS.uiT('customer.ordersQueryEmpty'))}</p>`;
          return;
        }
        const menuRef = fullMenu();
        const dishById = new Map((menuRef.dishes || []).map((dish) => [String(dish.id), dish]));
        const orderItemDisplayName = (it) => {
          const did = it?.dishId ?? it?.id;
          const d = did != null ? dishById.get(String(did)) : null;
          return d ? OS.dishNameForUiLang(menuRef, d) : String(it?.name ?? it?.dishId ?? '');
        };
        const orderItemImageSrc = (it) => {
          const did = it?.dishId ?? it?.id;
          const d = did != null ? dishById.get(String(did)) : null;
          const raw = d ? d.imageUrl || d.img || '' : '';
          const resolved = String(OS.resolveCustomerMediaUrl(raw) || '').trim();
          return resolved || DISH_IMG_EMPTY_PLACEHOLDER;
        };
        const renderOrder = (ord) => {
          const its = (ord.items || [])
            .map((it) => {
              const did = it?.dishId ?? it?.id;
              const dOrd = did != null ? dishById.get(String(did)) : null;
              const nameTone = 'text-neutral-900 dark:text-neutral-100';
              const imgSrc = orderItemImageSrc(it);
              const imgHtml = `<img src="${esc(imgSrc).replace(/"/g, '&quot;')}" alt="" class="h-10 w-10 shrink-0 rounded-md border border-neutral-200/80 bg-neutral-100 object-cover dark:border-neutral-700/80 dark:bg-neutral-800" width="40" height="40" loading="lazy" />`;
              const qtyHtml = `<span class="inline-flex min-w-[2.25rem] shrink-0 items-center justify-center rounded-md border border-neutral-200/85 bg-neutral-100 px-2 py-1 text-xs font-bold tabular-nums text-neutral-800 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100">${it.quantity ?? 0}×</span>`;
              const nameHtml = `<span class="min-w-0 flex-1 rounded-md border border-neutral-200/85 bg-white px-2 py-1 leading-snug dark:border-neutral-700/80 dark:bg-neutral-900/70 ${nameTone}">${esc(orderItemDisplayName(it))}</span>`;
              return `<li class="flex items-center gap-2 text-sm">${imgHtml}${qtyHtml}${nameHtml}</li>`;
            })
            .join('');
          const roundN = parseInt(String(ord.orderRound ?? 0), 10) || 0;
          const roundLabel = roundN
            ? OS.uiT('customer.badgeRound').replace(/\{\{n\}\}/g, String(roundN))
            : '';
          const totalNum = Number(ord.total);
          const totalLine =
            Number.isFinite(totalNum) && totalNum < 0
              ? ''
              : `<p class="mt-2 text-right text-sm font-semibold">€${ord.total ?? 0}</p>`;
          return (
            `<article class="rounded-lg border border-neutral-200/80 bg-white p-3 dark:border-neutral-700/80 dark:bg-neutral-900/80">` +
            (roundLabel
              ? `<p class="mb-1 text-[0.7rem] font-bold uppercase tracking-wider text-brand-700 dark:text-brand-300">${esc(roundLabel)}</p>`
              : '') +
            `<ul class="space-y-0.5">${its}</ul>` +
            totalLine +
            `</article>`
          );
        };
        const oneCol = renderSlots.length < 2;
        const curName = String(sess.guestName ?? '').trim();
        const cols = renderSlots.map((slot, idx) => {
          const guestOrders = list
            .filter((o) => String(o.guestName ?? '').trim() === slot)
            .sort((a, b) => {
              const ar = parseInt(String(a.orderRound ?? 0), 10) || 0;
              const br = parseInt(String(b.orderRound ?? 0), 10) || 0;
              if (ar !== br) return br - ar;
              return new Date(b.at || 0) - new Date(a.at || 0);
            });
          const inner = guestOrders.length
            ? guestOrders.map(renderOrder).join('')
            : `<p class="text-xs italic text-neutral-400 dark:text-neutral-500">${esc(OS.uiT('customer.ordersQueryEmpty'))}</p>`;
          const span = oneCol ? 'col-span-2' : '';
          const sep =
            !oneCol && idx % 2 === 1
              ? 'border-l border-neutral-200/80 pl-3 dark:border-neutral-700/80'
              : !oneCol && idx % 2 === 0
                ? 'pr-1'
                : '';
          const isCur = slot === curName;
          const headTone = isCur
            ? 'bg-brand-50 text-brand-800 dark:bg-brand-950/40 dark:text-brand-100 ring-1 ring-brand-500/30'
            : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800/70 dark:text-neutral-300';
          return (
            `<section class="min-w-0 ${span} ${sep}">` +
            `<h3 class="mb-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-extrabold tabular-nums shadow-sm ${headTone}">` +
            esc(String(slot)) +
            (isCur ? ' <span class="text-[0.65rem] font-bold uppercase tracking-wider opacity-80">·now</span>' : '') +
            `</h3>` +
            `<div class="space-y-2">${inner}</div>` +
            `</section>`
          );
        });
        el.innerHTML = `<div class="grid grid-cols-2 items-start gap-3">${cols.join('')}</div>`;
      };

      const closeOrdersQueryModal = () => {
        if (!$ordersQueryModal || $ordersQueryModal.classList.contains('hidden')) return;
        closeOrdersQueryGuestDropdown();
        closeGuestSwitchModalPopup();
        $ordersQueryModal.classList.add('hidden');
        $ordersQueryModal.setAttribute('aria-hidden', 'true');
        body.dataset.ordersQueryOpen = 'false';
        syncRootScrollLock();
      };

      const openOrdersQueryModal = () => {
        if (!$ordersQueryModal) return;
        closeWorkbarModal();
        closeOrdersQueryGuestDropdown();
        ordersQueryGuestFilter = null;
        syncOrdersQueryGuestBtn();
        renderOrdersQueryBody();
        $ordersQueryModal.classList.remove('hidden');
        $ordersQueryModal.setAttribute('aria-hidden', 'false');
        body.dataset.ordersQueryOpen = 'true';
        syncRootScrollLock();
      };

      function renderCart() {
        const menuRef = fullMenu();
        const { sum, count, rows } = cart.buildRenderSnapshot(menuRef);
        const y = '€' + sum;
        const tableCount = sumTableCartQty();
        const badgeDock = document.getElementById('cart-badge-dock');
        if (badgeDock) {
          badgeDock.textContent = tableCount > 99 ? '99+' : String(tableCount);
          badgeDock.classList.toggle('hidden', tableCount <= 0);
        }
        const badgeWb = document.getElementById('customer-workbar-cart-badge');
        if (badgeWb) {
          badgeWb.textContent = tableCount > 99 ? '99+' : String(tableCount);
          badgeWb.classList.toggle('hidden', tableCount <= 0);
        }
        const badgeNarrow = document.getElementById('customer-narrow-cart-badge');
        if (badgeNarrow) {
          badgeNarrow.textContent = tableCount > 99 ? '99+' : String(tableCount);
          badgeNarrow.classList.toggle('hidden', tableCount <= 0);
        }
        const badgeStrip = document.getElementById('customer-workbar-strip-cart-badge');
        if (badgeStrip) {
          badgeStrip.textContent = tableCount > 99 ? '99+' : String(tableCount);
          badgeStrip.classList.toggle('hidden', tableCount <= 0);
        }
        const badgeRail = document.getElementById('customer-workbar-rail-cart-badge');
        if (badgeRail) {
          badgeRail.textContent = tableCount > 99 ? '99+' : String(tableCount);
          badgeRail.classList.toggle('hidden', tableCount <= 0);
        }
        const badgeQuick = document.getElementById('customer-right-quickbar-cart-badge');
        if (badgeQuick) {
          badgeQuick.textContent = tableCount > 99 ? '99+' : String(tableCount);
          badgeQuick.classList.toggle('hidden', tableCount <= 0);
        }
        const sumWb = document.getElementById('customer-workbar-cart-summary');
        if (sumWb) {
          sumWb.textContent =
            tableCount > 0 ? `${y} · ${tableCount > 99 ? '99+' : String(tableCount)}×` : y;
        }
        const dockTot = document.getElementById('cart-dock-total');
        if (dockTot) dockTot.textContent = y;
        const dockMeta = document.getElementById('cart-dock-meta');
        if (dockMeta) dockMeta.textContent = count > 0 ? `${count}×` : '';
        if ($total) $total.textContent = y;
        const td = document.getElementById('cart-total-desktop');
        if (td) td.textContent = y;
        const remain = OS.getOrderCooldownRemainingMs();
        const dis = tableCount === 0 || remain > 0;
        if ($submit) $submit.disabled = dis;
        if ($submitDesktop) $submitDesktop.disabled = dis;
        const cartLocked = remain > 0;
        const linesHtml =
          rows.length === 0
            ? HtmlT.customerCartEmptyMessage(esc, OS.uiT('customer.cartEmpty'))
            : rows
                .map((r, ri) =>
                  HtmlT.customerCartLineRow(esc, {
                    ri,
                    name: OS.dishNameForUiLang(menuRef, r.d),
                    qty: r.qty,
                    line: r.line,
                    dishId: r.d.id,
                    imageUrl: OS.resolveCustomerMediaUrl(r.d.imageUrl || r.d.img || ''),
                    cartLocked,
                    foodClass: r.d.foodClass,
                  })
                )
                .join('');
        renderComponent([
          { el: $lines, html: linesHtml, after: bindCartQty },
          { el: $linesDesktop, html: linesHtml, after: bindCartQty },
        ]);
        if (pendingDetailDishId && body.dataset.dishDetailOpen === 'true') {
          syncDishDetailLabelsFromMenu();
          syncDishDetailQtyUi();
        }
        updateLockBanner();
        syncMenuDishQtyBadges();
        if (body.dataset.cartFullscreenOpen === 'true') renderCartFullscreenBody();
        if (body.dataset.ordersQueryOpen === 'true') renderOrdersQueryBody();
        syncCartCooldownHints();
        syncCartPopupsGuestSwitchBtn();
      }

      const addToCart = (dishId, qtyOpt) => {
        if (OS.getOrderCooldownRemainingMs() > 0) return updateLockBanner();
        const maxN = maxItemsAllowed();
        const maxT = maxItemsTableTotal();
        let add = parseInt(String(qtyOpt ?? 1), 10);
        if (!Number.isFinite(add) || add < 1) add = 1;
        if (maxN > 0 && cartTotalQty() + add > maxN) {
          alert(OS.uiT('customer.maxItemsReached').replace('{{n}}', String(maxN)));
          return;
        }
        if (maxT > 0 && sumTableCartQty() + add > maxT) {
          alert(formatMaxItemsTableMsg());
          return;
        }
        if (!fullMenu().dishes.find((x) => String(x?.id) === String(dishId))) return;
        cart.addN(dishId, add);
        dishDetailQty = 1;
        renderCart();
        setCartOpen(false);
        closeCartFullscreen();
      };

      /**
       * 真正提交退出：清当前会话 + 同步该桌历史，回到登录页。
       * 入口已改为「先弹密码确认窗」（见 showCustomerLogoutConfirm / requestCustomerLogout）。
       */
      const doCustomerLogoutCommit = () => {
        try {
          const sess = OS.getCustomerSession();
          const tbl = sess && sess.table != null ? String(sess.table).trim() : '';
          if (tbl) OS.clearRestaurantOrdersForTableAndBroadcast(tbl);
        } catch (_) {}
        try {
          OS.clearCustomerSession();
        } catch (_) {}
        cart.replaceFromRecord({});
        showLogin();
        /** 退出后回到密码闸（与首屏一致：闸 z-[500] 盖在登录层上） */
        const pwdGate = document.getElementById('customer-pwd-gate');
        if (pwdGate) {
          pwdGate.classList.remove('hidden');
          try {
            document.body.setAttribute('data-pwd-gate-open', '1');
          } catch (_) {}
          const boxes = pwdGate.querySelectorAll('.os-pwd-gate-box');
          boxes.forEach((b) => {
            try {
              b.value = '';
            } catch (_) {}
          });
          setTimeout(() => {
            try {
              boxes[0]?.focus();
            } catch (_) {}
          }, 40);
        }
        updateLockBanner();
      };

      /**
       * 退出确认密码：与密码闸同源（管理端「客户端登录密码」· customerLiteLoginPassword）。
       * 默认 123456；不存在或非 6 位数字则回退默认。
       */
      const readCustomerLogoutPassword = () => {
        try {
          const raw = localStorage.getItem('restaurantSystemSettings');
          if (!raw) return '123456';
          const s = JSON.parse(raw);
          const v = s && s.customerLiteLoginPassword != null ? String(s.customerLiteLoginPassword).trim() : '';
          return /^\d{6}$/.test(v) ? v : '123456';
        } catch (_) {
          return '123456';
        }
      };

      const customerLogoutBoxes = () => {
        const root = document.getElementById('customer-logout-confirm');
        if (!root) return [];
        return Array.from(root.querySelectorAll('[data-logout-pwd-idx]'));
      };
      const clearCustomerLogoutBoxes = () => {
        const boxes = customerLogoutBoxes();
        for (const b of boxes) {
          try { b.value = ''; } catch (_) {}
        }
      };
      const readCustomerLogoutCode = () => {
        const boxes = customerLogoutBoxes();
        let s = '';
        for (const b of boxes) s += String(b.value || '').replace(/\D/g, '').slice(0, 1);
        return s;
      };
      const showCustomerLogoutErr = (msg) => {
        const err = document.getElementById('customer-logout-confirm-err');
        if (!err) return;
        if (msg) {
          err.textContent = msg;
          err.classList.remove('hidden');
        } else {
          err.textContent = '';
          err.classList.add('hidden');
        }
      };
      const closeCustomerLogoutConfirm = () => {
        const root = document.getElementById('customer-logout-confirm');
        if (!root) return;
        root.classList.add('hidden');
        root.setAttribute('aria-hidden', 'true');
        clearCustomerLogoutBoxes();
        showCustomerLogoutErr('');
      };
      const validateCustomerLogoutPwd = () => {
        const code = readCustomerLogoutCode();
        if (code.length < 6) {
          showCustomerLogoutErr('Bitte 6 Ziffern eingeben');
          return;
        }
        if (code !== readCustomerLogoutPassword()) {
          showCustomerLogoutErr('Falsches Passwort');
          clearCustomerLogoutBoxes();
          const boxes = customerLogoutBoxes();
          if (boxes[0]) {
            try { boxes[0].focus(); } catch (_) {}
          }
          return;
        }
        closeCustomerLogoutConfirm();
        doCustomerLogoutCommit();
      };
      const showCustomerLogoutConfirm = () => {
        const root = document.getElementById('customer-logout-confirm');
        if (!root) {
          /** 兜底：DOM 缺失就退回直接登出，避免无路可走 */
          doCustomerLogoutCommit();
          return;
        }
        showCustomerLogoutErr('');
        clearCustomerLogoutBoxes();
        root.classList.remove('hidden');
        root.setAttribute('aria-hidden', 'false');
        setTimeout(() => {
          const boxes = customerLogoutBoxes();
          if (boxes[0]) {
            try { boxes[0].focus(); } catch (_) {}
          }
        }, 40);
      };
      /** 已绑定一次性事件防重复 */
      let customerLogoutConfirmBound = false;
      const bindCustomerLogoutConfirm = () => {
        if (customerLogoutConfirmBound) return;
        customerLogoutConfirmBound = true;
        const boxes = customerLogoutBoxes();
        boxes.forEach((el, idx) => {
          el.addEventListener('input', () => {
            const v = String(el.value || '').replace(/\D/g, '').slice(0, 1);
            el.value = v;
            if (v && idx < boxes.length - 1) {
              try { boxes[idx + 1].focus(); } catch (_) {}
            }
            if (idx === boxes.length - 1 && readCustomerLogoutCode().length === 6) validateCustomerLogoutPwd();
          });
          el.addEventListener('keydown', (e) => {
            if (e.key === 'Backspace' && !el.value && idx > 0) {
              try { boxes[idx - 1].focus(); } catch (_) {}
            } else if (e.key === 'Enter') {
              validateCustomerLogoutPwd();
            }
          });
        });
        document.getElementById('customer-logout-confirm-ok')?.addEventListener('click', (e) => {
          e.stopPropagation();
          validateCustomerLogoutPwd();
        });
        document.getElementById('customer-logout-confirm-cancel')?.addEventListener('click', (e) => {
          e.stopPropagation();
          closeCustomerLogoutConfirm();
        });
      };

      /** 旧调用入口替换为「先弹确认」。后端流程不变 */
      const doCustomerLogout = () => {
        bindCustomerLogoutConfirm();
        showCustomerLogoutConfirm();
      };

      const showLogin = () => {
        setCartOpen(false);
        closeCartFullscreen();
        closeOrdersQueryModal();
        closeGuestSwitchModalPopup();
        closeWorkbarModal();
        closeDishDetail();
        const fs = document.getElementById('customer-cart-fullscreen-modal');
        if (fs) {
          fs.classList.add('hidden');
          fs.setAttribute('aria-hidden', 'true');
        }
        body.dataset.cartFullscreenOpen = 'false';
        const gsm = document.getElementById('customer-guest-switch-modal');
        if (gsm) {
          gsm.classList.add('hidden');
          gsm.setAttribute('aria-hidden', 'true');
        }
        body.dataset.guestSwitchModalOpen = 'false';
        $loginScreen?.classList.remove('hidden');
        $customerApp?.classList.add('hidden');
        document.body.setAttribute('data-customer-login-open', '1');
        refreshCustomerStaticUi();
        syncRootScrollLock();
      };

      const showApp = () => {
        $loginScreen?.classList.add('hidden');
        $customerApp?.classList.remove('hidden');
        document.body.removeAttribute('data-customer-login-open');
        refreshSessionBadge();
      };

      const bootCustomerApp = () => {
        applyCustomerBranding();
        refreshCustomerStaticUi();
        menu = OS.getMenu();
        activeMeal = OS.getCustomerMealTab();
        pickValidMealTab();
        const sess0 = OS.getCustomerSession();
        if (sess0?.guestName && sess0.carts && sess0.carts[sess0.guestName]) {
          cart.replaceFromRecord(sess0.carts[sess0.guestName]);
        } else if (sess0?.guestName) {
          cart.replaceFromRecord({});
        }
        void refreshMenuUi();
      };

      runAfterCustomerLogin = () => {
        showApp();
        bootCustomerApp();
        updateLockBanner();
        connectCustomerOrderEventsWs();
        void hydrateTableCartFromServer();
      };

      const normalizeCustomerWsTableKey = (v) => String(v ?? '').trim();

      let customerLiveWs = null;
      let customerLiveWsUrl = '';

      const wsUrlFromCustomerBase = (base) => {
        try {
          const u = new URL(String(base || '').trim());
          const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
          return `${proto}//${u.host}/ws`;
        } catch {
          return '';
        }
      };

      const pickCustomerOrderEventsWsBase = () => {
        let b = (OS.getNetworkConfig().apiBaseUrl || '').trim().replace(/\/$/, '');
        if (!b && /^https?:/i.test(location.protocol)) b = location.origin.trim().replace(/\/$/, '');
        return b;
      };

      const connectCustomerOrderEventsWs = () => {
        if (typeof WebSocket === 'undefined') return;
        const base = pickCustomerOrderEventsWsBase();
        const url = wsUrlFromCustomerBase(base);
        if (!url) return;
        if (
          customerLiveWs &&
          customerLiveWsUrl === url &&
          (customerLiveWs.readyState === 0 || customerLiveWs.readyState === 1)
        )
          return;
        customerLiveWsUrl = url;
        try {
          customerLiveWs?.close();
        } catch (_) {}
        try {
          customerLiveWs = new WebSocket(url);
        } catch {
          customerLiveWs = null;
          return;
        }
        customerLiveWs.addEventListener('message', (ev) => {
          const raw = ev?.data;
          if (raw == null || raw === '') return;
          let msg;
          try {
            msg = JSON.parse(raw);
          } catch {
            return;
          }
          if (msg && typeof msg === 'object' && msg.type === 'table-cart-sync') {
            applyRemoteTableCartFromWs(msg);
            return;
          }
          if (msg && typeof msg === 'object' && msg.type === 'orders-log-reset') {
            try {
              const ROL = global.RestaurantOrderLib;
              if (ROL?.saveOrders) ROL.saveOrders([]);
            } catch (_) {}
            try {
              OS.clearAllCustomerGuestCarts();
              OS.setCustomerOrderRound(0);
            } catch (_) {}
            try {
              renderCart();
            } catch (_) {}
            try {
              if (body.dataset.ordersQueryOpen === 'true') renderOrdersQueryBody();
            } catch (_) {}
            OS.clearOrderCooldown();
            updateLockBanner();
            return;
          }
          if (!msg || typeof msg !== 'object' || msg.type !== 'table-history-cleared') return;
          const cleared = normalizeCustomerWsTableKey(msg.table);
          if (!cleared) return;
          const cur = normalizeCustomerWsTableKey(OS.getCustomerSession()?.table);
          if (!cur || cur !== cleared) return;
          try {
            const ROL = global.RestaurantOrderLib;
            if (ROL && typeof ROL.getOrders === 'function' && typeof ROL.saveOrders === 'function') {
              const list = ROL.getOrders() || [];
              const next = list.filter((o) => {
                const ot = o && o.table != null ? String(o.table).trim() : '';
                return ot !== cleared;
              });
              if (next.length !== list.length) ROL.saveOrders(next);
            }
          } catch (_) {}
          try {
            OS.clearAllCustomerGuestCarts();
            OS.setCustomerOrderRound(0);
          } catch (_) {}
          try {
            renderCart();
          } catch (_) {}
          try {
            if (body.dataset.ordersQueryOpen === 'true') renderOrdersQueryBody();
          } catch (_) {}
          OS.clearOrderCooldown();
          updateLockBanner();
        });
        customerLiveWs.addEventListener('close', () => {
          setTimeout(connectCustomerOrderEventsWs, 2500);
        });
      };

      try {
        window.addEventListener('storage', (e) => {
          if (e?.key !== 'restaurantNetworkConfig') return;
          connectCustomerOrderEventsWs();
        });
      } catch (_) {}

      const reloadMenuFromStorage = () => {
        menu = OS.getMenu();
        activeMeal = OS.getCustomerMealTab();
        pickValidMealTab();
        cart.pruneToMenu(menu);
        syncLoginMealUi();
        void refreshMenuUi();
      };

      updateThemeToggleLabel();

      /** URL ?table=05 等：扫码进店时自动写入桌号并进入点餐（可选 ?guests=2） */
      void (async () => {
        try {
          const params = new URLSearchParams(window.location.search);
          const tableRaw = params.get('table');
          if (tableRaw == null || !String(tableRaw).trim()) return;
          const table = String(tableRaw).trim().slice(0, 32);
          if (!table) return;
          let guests = parseInt(String(params.get('guests') || ''), 10);
          if (!Number.isFinite(guests) || guests < 1 || guests > 99) guests = 2;
          const guestParam = params.get('guest');
          const guestName = OS.resolveGuestFromUrlParam(guestParam, table, guests);
          const mealParam = params.get('meal');
          if (mealParam === 'lunch' || mealParam === 'dinner' || mealParam === 'special') {
            OS.setCustomerMealTab(mealParam);
          }
          const allocation = await allocateCustomerGuestSlots(table, guests);
          OS.setCustomerSession(
            table,
            allocation?.totalGuests || guests,
            allocation?.guestName || guestName,
            allocation ? { startIndex: allocation.startIndex, guests: allocation.assignedGuests } : undefined
          );
          if (!allocation) {
            try {
              g.OS?.hooks?.notifyTableOccupied?.();
            } catch (_) {
              /* 同上，扫码/URL 进店 */
            }
          }
          const url = new URL(window.location.href);
          url.searchParams.delete('table');
          url.searchParams.delete('guests');
          url.searchParams.delete('guest');
          url.searchParams.delete('meal');
          const q = url.searchParams.toString();
          const next = url.pathname + (q ? '?' + q : '') + url.hash;
          window.history.replaceState(null, '', next);
        } catch (_) {
          /* ignore */
        }
      })();

      document.querySelectorAll('.js-theme-toggle').forEach((btn) => {
        btn.addEventListener('click', () => {
          const root = document.documentElement;
          root.classList.toggle('dark');
          try {
            localStorage.setItem('restaurantCustomerTheme', root.classList.contains('dark') ? 'dark' : 'light');
          } catch (_) {}
          updateThemeToggleLabel();
        });
      });

      window.addEventListener('resize', onLayoutViewportChange);
      window.addEventListener('orientationchange', onLayoutViewportChange);
      placeWorkbarMenuHost();
      prevWideCartLayout = isWideCartLayout();

      $workbarModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-workbar-modal-dismiss]')) closeWorkbarModal();
      });
      $customerApp?.addEventListener(
        'click',
        (e) => {
          const op = e.target.closest('[data-workbar-modal-open]');
          if (!op) return;
          e.preventDefault();
          const t = op.getAttribute('data-workbar-modal-open');
          if (t === 'cart') {
            openCartFullscreenFromUserGesture();
            return;
          }
          if (t === 'menu' || t === 'lang' || t === 'account') openWorkbarModal(t);
        },
        true
      );

      $customerApp?.addEventListener(
        'touchstart',
        (e) => {
          const t = e.target;
          if (!(t instanceof Element)) return;
          if (!t.closest('[data-workbar-modal-open="cart"]')) return;
          openCartFullscreenFromUserGesture();
        },
        { capture: true, passive: true }
      );

      $customerApp?.addEventListener(
        'touchend',
        (e) => {
          if (!e.changedTouches || !e.changedTouches.length) return;
          const t = e.target;
          if (!(t instanceof Element)) return;
          if (!t.closest('[data-workbar-modal-open="cart"]')) return;
          e.preventDefault();
          openCartFullscreenFromUserGesture();
        },
        { passive: false }
      );

      $loginTable?.addEventListener('input', syncLoginGuestSlots);
      $loginTable?.addEventListener('change', syncLoginGuestSlots);
      $loginGuests?.addEventListener('input', syncLoginGuestSlots);
      $loginGuests?.addEventListener('change', syncLoginGuestSlots);
      syncLoginGuestSlots();

      const scrollLoginSubmitIntoView = () => {
        const sub = document.getElementById('customer-login-submit');
        const wrap = document.getElementById('customer-login-scroll');
        if (!sub || !wrap) return;
        requestAnimationFrame(() => {
          try {
            sub.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          } catch (_) {
            sub.scrollIntoView(false);
          }
        });
      };
      const coarsePointer =
        typeof globalThis.matchMedia === 'function' &&
        globalThis.matchMedia('(pointer: coarse)').matches === true;
      if (coarsePointer) {
        document.getElementById('customer-login-form')?.addEventListener(
          'focusin',
          (e) => {
            const t = e.target;
            if (!(t instanceof HTMLElement)) return;
            if (t.closest('#customer-login-guest-slots')) scrollLoginSubmitIntoView();
            else if (t.id === 'customer-login-table' || t.id === 'customer-login-guests') scrollLoginSubmitIntoView();
          },
          true
        );
        globalThis.visualViewport?.addEventListener?.('resize', () => {
          if (!$loginScreen || $loginScreen.classList.contains('hidden')) return;
          const ae = document.activeElement;
          if (
            ae &&
            (ae.id === 'customer-login-table' ||
              ae.id === 'customer-login-guests' ||
              (ae instanceof HTMLElement && ae.closest('#customer-login-guest-slots')))
          ) {
            scrollLoginSubmitIntoView();
          }
        });
      }

      document.getElementById('customer-login-meal-tabs')?.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-login-meal]');
        if (!btn) return;
        const tab = btn.getAttribute('data-login-meal');
        if (tab !== 'lunch' && tab !== 'dinner' && tab !== 'special') return;
        OS.setCustomerMealTab(tab);
        syncLoginMealUi();
      });

      $guestSwitchWrapWb?.addEventListener('click', (e) => e.stopPropagation());
      $guestAccountBtnsNarrow?.addEventListener('click', (e) => e.stopPropagation());
      guestSwitchBtns().forEach((btn) => {
        btn?.addEventListener('click', (e) => {
          e.stopPropagation();
          const hidden = guestSwitchMenus().every((m) => m.classList.contains('hidden'));
          if (hidden) openGuestSwitchMenu();
          else closeGuestSwitchMenu();
        });
      });
      document.addEventListener('click', () => closeGuestSwitchMenu());

      document.getElementById('customer-narrow-cart-strip-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openCartFullscreenFromUserGesture();
      });
      document.getElementById('customer-narrow-cart-strip-btn')?.addEventListener('touchstart', () => openCartFullscreenFromUserGesture(), {
        passive: true,
      });
      document.getElementById('customer-narrow-cart-strip-btn')?.addEventListener(
        'touchend',
        (e) => {
          if (!e.changedTouches || !e.changedTouches.length) return;
          e.stopPropagation();
          e.preventDefault();
          openCartFullscreenFromUserGesture();
        },
        { passive: false }
      );

      globalThis.addEventListener('pagehide', () => {
        try {
          persistGuestCartNow();
        } catch (_) {}
      });

      window.addEventListener('storage', (e) => {
        const { key } = e;
        const { KEYS } = OS;
        const onLang = () => {
          refreshCustomerStaticUi();
          void refreshMenuUi();
        };
        const byKey = new Map([
          [KEYS.MENU, reloadMenuFromStorage],
          [KEYS.SYSTEM_SETTINGS, reloadMenuFromStorage],
          [KEYS.RESTAURANT_INFO, applyCustomerBranding],
          [KEYS.UI_LANG, onLang],
        ]);
        byKey.get(key)?.();
      });
      window.addEventListener('restaurantMenuUpdated', reloadMenuFromStorage);
      window.addEventListener('restaurantSystemSettingsUpdated', reloadMenuFromStorage);
      window.addEventListener('restaurantInfoUpdated', applyCustomerBranding);
      OS.emitter.on('menu:updated', reloadMenuFromStorage);

      document.getElementById('btn-mobile-selected-bar')?.addEventListener('click', () => openCartFullscreenFromUserGesture());
      document.getElementById('btn-mobile-selected-bar')?.addEventListener(
        'touchstart',
        () => {
          openCartFullscreenFromUserGesture();
        },
        { passive: true }
      );
      document.getElementById('btn-mobile-selected-bar')?.addEventListener(
        'touchend',
        (e) => {
          if (!e.changedTouches || !e.changedTouches.length) return;
          e.preventDefault();
          openCartFullscreenFromUserGesture();
        },
        { passive: false }
      );
      document.getElementById('customer-workbar-expand-toggle')?.addEventListener('click', (e) => {
        e.stopPropagation();
        setCustomerWorkbarCollapsed(false);
      });
      document.getElementById('customer-workbar-collapse-toggle')?.addEventListener('click', (e) => {
        e.stopPropagation();
        setCustomerWorkbarCollapsed(true);
      });
      document.getElementById('customer-workbar-rail-cart-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openCartFullscreenFromUserGesture();
      });
      document.getElementById('customer-workbar-rail-cart-btn')?.addEventListener('touchstart', () => openCartFullscreenFromUserGesture(), {
        passive: true,
      });
      document.getElementById('customer-workbar-rail-cart-btn')?.addEventListener(
        'touchend',
        (e) => {
          if (!e.changedTouches || !e.changedTouches.length) return;
          e.stopPropagation();
          e.preventDefault();
          openCartFullscreenFromUserGesture();
        },
        { passive: false }
      );
      document.getElementById('customer-workbar-rail-orders-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openOrdersQueryModal();
      });
      document.getElementById('customer-workbar-strip-cart-open-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openCartFullscreenFromUserGesture();
      });
      document.getElementById('customer-workbar-strip-cart-open-btn')?.addEventListener('touchstart', () => openCartFullscreenFromUserGesture(), {
        passive: true,
      });
      document.getElementById('customer-workbar-strip-cart-open-btn')?.addEventListener(
        'touchend',
        (e) => {
          if (!e.changedTouches || !e.changedTouches.length) return;
          e.stopPropagation();
          e.preventDefault();
          openCartFullscreenFromUserGesture();
        },
        { passive: false }
      );
      document.getElementById('customer-workbar-cart-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openCartFullscreenFromUserGesture();
      });
      document.getElementById('customer-workbar-cart-btn')?.addEventListener('touchstart', () => openCartFullscreenFromUserGesture(), {
        passive: true,
      });
      document.getElementById('customer-workbar-cart-btn')?.addEventListener(
        'touchend',
        (e) => {
          if (!e.changedTouches || !e.changedTouches.length) return;
          e.stopPropagation();
          e.preventDefault();
          openCartFullscreenFromUserGesture();
        },
        { passive: false }
      );
      document.getElementById('customer-cart-fs-close')?.addEventListener('click', () => closeCartFullscreen());
      const openGuestSwitchFromCartPopup = (e) => {
        e.stopPropagation();
        openGuestSwitchModalPopup();
      };
      document.getElementById('customer-cart-fs-guest-switch')?.addEventListener('click', openGuestSwitchFromCartPopup);
      document.getElementById('cart-drawer-guest-switch')?.addEventListener('click', openGuestSwitchFromCartPopup);
      document.getElementById('customer-orders-query-close')?.addEventListener('click', () => closeOrdersQueryModal());
      document.getElementById('customer-narrow-orders-query-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openOrdersQueryModal();
      });
      document.getElementById('customer-narrow-logout-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        doCustomerLogout();
      });
      document.getElementById('customer-narrow-workbar-collapse')?.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        applyNarrowWorkbarCollapsed(true);
      });
      document.getElementById('customer-narrow-workbar-expand')?.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        applyNarrowWorkbarCollapsed(false);
      });
      applyNarrowWorkbarCollapsed();

      /** 窄屏顶栏：收起时从顶部下拉展开（不打断正常滚动，仅在顶部手势明显时触发） */
      (() => {
        const wrap = document.getElementById('customer-narrow-workbar-wrap');
        if (!wrap || wrap.dataset.pullToExpandBound === '1') return;
        wrap.dataset.pullToExpandBound = '1';
        let startX = 0;
        let startY = 0;
        let tracking = false;
        const canExpandNow = () => {
          const expanded = document.getElementById('customer-narrow-workbar-expanded');
          const expandBtn = document.getElementById('customer-narrow-workbar-expand');
          if (!expanded || !expandBtn) return false;
          if (expandBtn.classList.contains('hidden')) return false;
          if (body.dataset.dishDetailOpen === 'true') return false;
          if (body.dataset.workbarModalOpen === 'true') return false;
          if (body.dataset.cartFullscreenOpen === 'true') return false;
          if (body.dataset.ordersQueryOpen === 'true') return false;
          if (body.dataset.guestSwitchModalOpen === 'true') return false;
          return true;
        };
        document.addEventListener(
          'touchstart',
          (e) => {
            if (!e.touches || e.touches.length !== 1) return;
            const t = e.touches[0];
            if (!t) return;
            startX = t.clientX;
            startY = t.clientY;
            tracking = canExpandNow() && startY <= 36;
          },
          { passive: true }
        );
        document.addEventListener(
          'touchmove',
          (e) => {
            if (!tracking || !e.touches || e.touches.length !== 1) return;
            const t = e.touches[0];
            if (!t) return;
            const dx = t.clientX - startX;
            const dy = t.clientY - startY;
            if (dy > 34 && Math.abs(dx) < 22) {
              tracking = false;
              applyNarrowWorkbarCollapsed(false);
            }
          },
          { passive: true }
        );
        document.addEventListener(
          'touchend',
          () => {
            tracking = false;
          },
          { passive: true }
        );
        document.addEventListener(
          'touchcancel',
          () => {
            tracking = false;
          },
          { passive: true }
        );
      })();
      document.getElementById('customer-workbar-orders-query-open-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openOrdersQueryModal();
      });

      /**
       * 右栏 Quickbar：顾客账号下拉 + 点单记录。
       * 购物车按钮已通过 [data-workbar-modal-open="cart"] 走全局派发，无需单独绑定。
       */
      const rightQuickbarTrigger = document.getElementById('customer-right-quickbar-guest-trigger');
      const rightQuickbarMenu = document.getElementById('customer-right-quickbar-guest-menu');
      const openRightQuickbarGuestMenu = () => {
        if (!rightQuickbarMenu || !rightQuickbarTrigger) return;
        const sess = OS.getCustomerSession();
        const slots = switchableGuestSlots(sess);
        if (slots.length <= 1) return;
        renderRightQuickbarGuest(sess);
        rightQuickbarMenu.classList.remove('hidden');
        rightQuickbarMenu.setAttribute('aria-hidden', 'false');
        rightQuickbarTrigger.setAttribute('aria-expanded', 'true');
      };
      rightQuickbarTrigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = rightQuickbarMenu && !rightQuickbarMenu.classList.contains('hidden');
        if (open) closeRightQuickbarGuestMenu();
        else openRightQuickbarGuestMenu();
      });
      rightQuickbarMenu?.addEventListener('click', (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        const btn = t.closest('[data-right-quick-guest-pick]');
        if (!btn) return;
        e.stopPropagation();
        const slot = btn.getAttribute('data-right-quick-guest-pick') || '';
        const curS = OS.getCustomerSession();
        if (!curS || !slot || slot === String(curS.guestName ?? '').trim()) {
          closeRightQuickbarGuestMenu();
          return;
        }
        const from = curS.guestName;
        const snap = cart.toQuantityRecord();
        const loaded = OS.applyCustomerGuestSwitch(from, snap, slot);
        cart.replaceFromRecord(loaded || {});
        refreshSessionBadge();
        renderCart();
        syncMenuDishQtyBadges();
        closeRightQuickbarGuestMenu();
      });
      document.addEventListener('click', (e) => {
        if (!rightQuickbarMenu || rightQuickbarMenu.classList.contains('hidden')) return;
        const t = e.target;
        if (!(t instanceof Element)) return;
        if (t.closest('#customer-right-quickbar-guest-menu')) return;
        if (t.closest('#customer-right-quickbar-guest-trigger')) return;
        closeRightQuickbarGuestMenu();
      });
      document.getElementById('customer-right-quickbar-orders-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openOrdersQueryModal();
      });
      /**
       * Service / Bezahlen 呼叫：与 customer-lite 逻辑一致，
       * POST /api/table-call { kind: 'service'|'bill', state: 'set' }。
       * 后台「餐厅现时管理」该台子立即变色 + 脉冲，直至管理员点击该台清除。
       * 给按钮 2s 视觉反馈避免重复触发。
       */
      const sendCustomerTableCall = async (kind, btnEl) => {
        try {
          const sess = OS.getCustomerSession();
          const t = sess && sess.table != null ? String(sess.table).trim() : '';
          if (!t) return;
          if (kind !== 'service' && kind !== 'bill') return;
          let base = (OS.getNetworkConfig().apiBaseUrl || '').trim().replace(/\/$/, '');
          if (!base && /^https?:/i.test(location.protocol)) base = location.origin;
          if (!base) return;
          if (btnEl?.classList) {
            if (btnEl.getAttribute('aria-busy') === 'true') return;
            btnEl.classList.add('is-pending');
            btnEl.setAttribute('aria-busy', 'true');
          }
          const api = globalThis.RestaurantApiService;
          if (api?.postJson) {
            await api.postJson(base + '/api/table-call', { table: t, kind, state: 'set' }, { timeoutMs: 8000, maxRetries: 1 });
          } else {
            const res = await fetch(base + '/api/table-call', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ table: t, kind, state: 'set' }),
              mode: 'cors',
            });
            if (!res.ok) throw new Error('table-call failed');
          }
          showCustomerToast(kind === 'service' ? 'Service gerufen!' : 'Bezahlen gerufen!');
        } catch (_) {
          showCustomerToast('Konnte nicht gesendet werden. Bitte erneut versuchen.', 'error');
        } finally {
          try {
            btnEl?.classList?.remove('is-pending');
            btnEl?.removeAttribute?.('aria-busy');
          } catch (_) {}
        }
      };
      document.getElementById('customer-workbar-call-service-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        void sendCustomerTableCall('service', e.currentTarget);
      });
      document.getElementById('customer-workbar-call-bill-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        void sendCustomerTableCall('bill', e.currentTarget);
      });
      document.getElementById('customer-workbar-logout-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        doCustomerLogout();
      });
      document.getElementById('customer-orders-query-guest-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const menu = document.getElementById('customer-orders-query-guest-menu');
        const btn = document.getElementById('customer-orders-query-guest-btn');
        if (!menu || !btn) return;
        if (menu.classList.contains('hidden')) {
          syncOrdersQueryGuestMenu();
          menu.classList.remove('hidden');
          menu.setAttribute('aria-hidden', 'false');
          btn.setAttribute('aria-expanded', 'true');
        } else closeOrdersQueryGuestDropdown();
      });
      $ordersQueryModal?.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-orders-query-guest]');
        if (!chip || !chip.closest('#customer-orders-query-guest-menu')) return;
        e.preventDefault();
        const raw = chip.getAttribute('data-orders-query-guest');
        if (raw === '__all__') ordersQueryGuestFilter = null;
        else {
          try {
            ordersQueryGuestFilter = decodeURIComponent(raw ?? '');
          } catch {
            ordersQueryGuestFilter = raw;
          }
        }
        closeOrdersQueryGuestDropdown();
        syncOrdersQueryGuestBtn();
        renderOrdersQueryBody();
      });
      document.addEventListener('click', (e) => {
        const menu = document.getElementById('customer-orders-query-guest-menu');
        const wrap = document.getElementById('customer-orders-query-guest-wrap');
        if (!menu || menu.classList.contains('hidden')) return;
        const t = e.target;
        if (t instanceof Node && wrap?.contains(t)) return;
        closeOrdersQueryGuestDropdown();
      });
      document.getElementById('customer-sidebar-guest-switch-open')?.addEventListener('click', (e) => {
        e.stopPropagation();
        openGuestSwitchModalPopup();
      });
      $guestSwitchModal?.addEventListener('click', (e) => {
        if (e.target.closest('[data-guest-switch-modal-dismiss]')) closeGuestSwitchModalPopup();
      });
      document.getElementById('btn-close-cart')?.addEventListener('click', () => setCartOpen(false));
      document.getElementById('btn-close-dish-detail')?.addEventListener('click', closeDishDetail);
      document.getElementById('dish-detail-overlay')?.addEventListener('click', closeDishDetail);
      document.getElementById('dish-detail-qty-dec')?.addEventListener('click', () => {
        if (dishDetailQty > 1) {
          dishDetailQty--;
          syncDishDetailQtyUi();
        }
      });
      document.getElementById('dish-detail-qty-inc')?.addEventListener('click', () => {
        const cap = detailQtyMaxAdd();
        if (cap > 0 && dishDetailQty < cap) {
          dishDetailQty++;
          syncDishDetailQtyUi();
        }
      });
      document.getElementById('dish-detail-add')?.addEventListener('click', () => {
        if (!pendingDetailDishId) return;
        playAddToCartFly(document.getElementById('dish-detail-img'));
        addToCart(pendingDetailDishId, dishDetailQty);
        closeDishDetail();
      });
      const printFailDetail = (pr) => {
        const r0 = pr?.results?.[0];
        if (!r0) return '';
        const d = r0.data;
        if (d && typeof d === 'object' && d.error != null) return String(d.error);
        if (typeof d === 'string') return d.slice(0, 240);
        return r0.status != null ? 'HTTP ' + r0.status : '';
      };

      let tableOrderSubmitBusy = false;

      const buildKitchenMetaForApi = () => {
        const cfg = OS.getPrintConfig && OS.getPrintConfig();
        const uiLang = typeof OS.getUiLang === 'function' ? OS.getUiLang() : 'de';
        if (!cfg || typeof cfg !== 'object') {
          return {
            printMode: 'summary',
            printerConfig: { default: 'default' },
            categoryPrintMode: {},
            ticketStyle: {},
            ticketUiLang: uiLang,
          };
        }
        const ctp = cfg.categoryToPrinter && typeof cfg.categoryToPrinter === 'object' ? { ...cfg.categoryToPrinter } : {};
        if (ctp.default == null || String(ctp.default).trim() === '') ctp.default = 'default';
        const cpmThermal =
          cfg.categoryPrintMode && typeof cfg.categoryPrintMode === 'object' ? { ...cfg.categoryPrintMode } : {};
        let cpmSys = {};
        try {
          const RCL = global.RestaurantConfigLib;
          if (RCL && typeof RCL.getSystemSettings === 'function' && typeof RCL.normalizeKitchenCategoryPrintMode === 'function') {
            cpmSys = RCL.normalizeKitchenCategoryPrintMode(RCL.getSystemSettings().kitchenCategoryPrintMode);
          }
        } catch (_k) {}
        const cpm = { ...cpmThermal, ...cpmSys };
        return {
          printMode: cfg.printMode === 'single' ? 'single' : 'summary',
          printerConfig: ctp,
          categoryPrintMode: cpm,
          ticketStyle: cfg.ticketStyle && typeof cfg.ticketStyle === 'object' ? cfg.ticketStyle : {},
          ticketUiLang: uiLang,
        };
      };

      const postOneCustomerOrderPipeline = async (order) => {
        let base = (OS.getNetworkConfig().apiBaseUrl || '').trim().replace(/\/$/, '');
        if (!base && /^https?:/i.test(location.protocol)) base = location.origin;
        let postResult = null;
        const orderWithKitchen = { ...order, kitchen: buildKitchenMetaForApi() };
        if (base) {
          try {
            postResult = await OS.postCustomerOrderToBackend(orderWithKitchen, base);
          } catch (_) {}
        }
        const dispatch = OS.dispatchOrder(order);
        const proxyUrl = (OS.getNetworkConfig().printProxyUrl || '').trim();
        if (proxyUrl) {
          try {
            await OS.postPrintDispatch(dispatch, proxyUrl);
          } catch (_) {}
        }
        let printOk = true;
        let printDetail = '';
        let serverHandledPrint = false;
        if (postResult && postResult.ok && postResult.data && typeof postResult.data === 'object') {
          const pd = postResult.data;
          if (Object.prototype.hasOwnProperty.call(pd, 'print')) {
            const prn = pd.print;
            if (prn && prn.ok === true) {
              serverHandledPrint = true;
              printOk = true;
            } else if (prn && prn.ok === false && prn.error) {
              serverHandledPrint = true;
              printOk = false;
              printDetail = String(prn.error);
            }
          }
        }
        if (!serverHandledPrint) {
          try {
            const pr = await OS.sendThermalOrderViaPrintLib(order);
            printOk = !!(pr && pr.ok);
            if (!printOk) printDetail = printFailDetail(pr);
          } catch (e) {
            printOk = false;
            printDetail = String(e?.message ?? e);
          }
        }
        return { printOk, printDetail };
      };

      const doSubmitTableOrders = async () => {
        if (!OS.getCustomerSession()) return showLogin();
        const cdMs = OS.getOrderCooldownRemainingMs();
        if (cdMs > 0) {
          const hint = orderCooldownHumanHint();
          alert(hint ? `${OS.uiT('customer.orderLockBanner')}\n${hint}` : OS.uiT('customer.orderLockBanner'));
          return;
        }
        if (tableOrderSubmitBusy) return;
        persistGuestCartNow();
        const sess = OS.getCustomerSession();
        if (!sess) return;
        const menuSnap = fullMenu();
        const tmp = Cart.createSessionCart();
        const maxN = maxItemsAllowed();
        const maxT = maxItemsTableTotal();
        const live = cart.toQuantityRecord();
        const payloads = [];
        let pi = 0;
        if (maxT > 0 && sumTableCartQty() > maxT) {
          alert(formatMaxItemsTableMsg());
          return;
        }
        const orderSlots = switchableGuestSlots(sess);
        for (const slot of orderSlots) {
          const rec = slot === sess.guestName ? live : (sess.carts?.[slot] ?? {});
          tmp.replaceFromRecord(rec);
          let { items, total } = tmp.buildOrderPayload(menuSnap);
          items = items.map((it) => {
            const d = (menuSnap.dishes || []).find((x) => String(x.id) === String(it.dishId));
            return d ? { ...it, name: OS.dishNameForUiLang(menuSnap, d) } : it;
          });
          if (!items.length) continue;
          const tq = tmp.totalQuantity();
          if (maxN > 0 && tq > maxN) {
            alert(`${OS.uiT('customer.maxItemsReached').replace('{{n}}', String(maxN))}\n— ${slot}`);
            return;
          }
          payloads.push({ guestName: slot, items, total, pi: pi++ });
        }
        if (!payloads.length) {
          alert(OS.uiT('customer.cartEmpty'));
          return;
        }
        const lines = payloads.map((p) => `${p.guestName}: €${p.total}`).join('\n');
        if (!window.confirm(`${OS.uiT('customer.confirmTableOrder')}\n${lines}`)) return;
        tableOrderSubmitBusy = true;
        try {
          /**
           * 下单瞬间先同步一次 server 的 customer-system-settings.json，
           * 避免 iOS 长时间开屏、visibilitychange 未触发时锁屏时长取到旧值
           * （与 customer-lite 的做法一致）。失败时退回当前 localStorage 缓存。
           */
          if (typeof OS.refreshSystemSettingsFromServer === 'function') {
            await OS.refreshSystemSettingsFromServer();
          }
          OS.setOrderCooldownMinutes(OS.getSystemSettings().lockMinutesBetweenOrders);
          updateLockBanner();
          const rn = (OS.getRestaurantInfo().name || 'YUOKI GmbH').trim() || 'YUOKI GmbH';
          const orderRound = (sess.orderRound ?? 0) + 1;
          let anyPrintFail = false;
          let lastDetail = '';
          const t0 = Date.now();
          for (const p of payloads) {
            const order = {
              id: `ord_${t0}_${p.pi}_${String(p.guestName).replace(/\W/g, '_')}`,
              at: new Date().toISOString(),
              restaurantName: rn,
              table: sess.table,
              guestCount: sess.guests,
              guestName: p.guestName,
              items: p.items,
              total: p.total,
              orderRound,
            };
            OS.addOrder(order);
            const { printOk, printDetail } = await postOneCustomerOrderPipeline(order);
            if (!printOk) {
              anyPrintFail = true;
              lastDetail = printDetail;
            }
          }
          OS.setCustomerOrderRound(orderRound);
          refreshSessionBadge();
          OS.clearCustomerGuestCarts?.(payloads.map((p) => p.guestName));
          cart.clear();
          void pushTableCartToServer();
          closeCartFullscreen();
          setCartOpen(false);
          void menuUi.renderMenu();
          renderCart();
          if (anyPrintFail)
            alert(
              OS.uiT('customer.printKitchenWarn').replace(
                '{{detail}}',
                lastDetail.trim() || OS.uiT('customer.printKitchenWarnUnknown')
              )
            );
          else alert(OS.uiT('customer.orderDone'));
        } finally {
          tableOrderSubmitBusy = false;
        }
      };

      document.getElementById('customer-cart-fs-submit')?.addEventListener('click', () => void doSubmitTableOrders());

      [$submit, $submitDesktop].filter(Boolean).forEach((btn) => btn.addEventListener('click', () => void doSubmitTableOrders()));

      setInterval(updateLockBanner, 1000);

      const HYDRATE_WAIT_MS = 10000;
      await Promise.race([
        hydratePromise.then(() => {}).catch(() => {}),
        new Promise((resolve) => {
          setTimeout(resolve, HYDRATE_WAIT_MS);
        }),
      ]);

      menu = fullMenu();

      if (OS.getCustomerSession()) {
        showApp();
        bootCustomerApp();
      } else {
        showLogin();
      }
      updateLockBanner();
      connectCustomerOrderEventsWs();
      if (OS.getCustomerSession()) void hydrateTableCartFromServer();
      onLayoutViewportChange();
      /** 横屏刷新：Safari 首帧 innerHeight / safe-area 常滞后，延迟再同步冷却锁层与顶栏变量 */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateLockBanner();
          onLayoutViewportChange();
          syncCustomerHeaderHeight();
        });
      });
      setTimeout(() => {
        updateLockBanner();
        syncCustomerHeaderHeight();
      }, 200);
    })();
  }

  g.initCustomerPage = initCustomerPage;
  g.RestaurantCustomerUi = {
    initCustomerPage,
    showLoading() {},
    hideLoading() {},
  };

  if (g.OS && typeof g.OS.registerModule === 'function') {
    try { g.OS.registerModule('ui', g.RestaurantCustomerUi); } catch (_) {}
  }
})(typeof globalThis !== 'undefined' ? globalThis : window);
